import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  MarkerType,
  ConnectionLineType,
  MiniMap,
  ConnectionMode,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import NodeSettingsPanel from "../components/NodeSettingsPanel";
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  IconButton,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  FormControlLabel,
  FormLabel,
  FormGroup,
  Switch,
  Radio,
  RadioGroup,
  Checkbox,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Alert,
} from "@mui/material";

// Icons
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import StrategyEngineService from "../services/StrategyEngineService";
import LaunchIcon from "@mui/icons-material/Launch";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ArticleIcon from "@mui/icons-material/Article";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HistoryIcon from "@mui/icons-material/History";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import MemoryIcon from "@mui/icons-material/Memory";
// removed unused import
import DataUsageIcon from "@mui/icons-material/DataUsage";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CodeIcon from "@mui/icons-material/Code";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import TimerIcon from "@mui/icons-material/Timer";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssessmentIcon from "@mui/icons-material/Assessment";
import BarChartIcon from "@mui/icons-material/BarChart";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TelegramIcon from "@mui/icons-material/Telegram";
import EmailIcon from "@mui/icons-material/Email";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EditIcon from "@mui/icons-material/Edit";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import InsightsIcon from "@mui/icons-material/Insights";

// TradingView symbol mapping for rebranded tokens and correct exchange formats
const getTradingViewSymbol = (symbol, exchange = 'KUCOIN') => {
  // Normalize the symbol (remove separators)
  const normalized = symbol?.replace('-', '').replace('/', '').toUpperCase() || 'BTCUSDT';
  
  // Symbol mappings for rebranded/delisted tokens
  const symbolMap = {
    'MATICUSDT': 'POLUSDT',      // MATIC rebranded to POL
    'MATIC': 'POL',
    'LUNAUSDT': 'LUNCUSDT',      // LUNA -> LUNC (classic)
    'LUNA': 'LUNC',
    'USTUSDT': 'USTCUSDT',       // UST -> USTC
    'UST': 'USTC',
  };
  
  // Check if we need to remap
  const mappedSymbol = symbolMap[normalized] || normalized;
  
  // Ensure it ends with USDT for crypto pairs
  const finalSymbol = mappedSymbol.endsWith('USDT') ? mappedSymbol : `${mappedSymbol}USDT`;
  
  // Exchange prefix mapping
  const exchangeMap = {
    'BINANCE': 'BINANCE',
    'KUCOIN': 'KUCOIN',
    'COINBASE': 'COINBASE',
    'KRAKEN': 'KRAKEN',
    'BYBIT': 'BYBIT',
  };
  
  const exchangePrefix = exchangeMap[exchange.toUpperCase()] || 'KUCOIN';
  
  return `${exchangePrefix}:${finalSymbol}`;
};

// Node types for the strategy builder - defined outside component to prevent recreation on each render
const nodeTypes = {
  technicalAnalyst: TechnicalAnalystNode,
  fundamentalAnalyst: FundamentalAnalystNode,
  riskManager: RiskManagerNode,
  tradeHistorian: TradeHistorianNode,
  reportSynthesizer: ReportSynthesizerNode,
  executionLayer: ExecutionLayerNode,
  dataSource: DataSourceNode,
  condition: ConditionNode,
  memory: MemoryNode,
  telegramReport: TelegramReportNode,
  scheduler: SchedulerNode,
  aiSchedule: AIScheduleNode,
  signalMatrix: SignalMatrixNode,
};

// Node component definitions
function TechnicalAnalystNode({ data }) {
  return (
    <NodeCard
      title="Technical Analyst"
      icon={<ShowChartIcon />}
      color="#3f51b5"
      data={data}
    />
  );
}

function FundamentalAnalystNode({ data }) {
  return (
    <NodeCard
      title="Fundamental Analyst"
      icon={<ArticleIcon />}
      color="#f50057"
      data={data}
    />
  );
}

function RiskManagerNode({ data }) {
  return (
    <NodeCard
      title="Risk Manager"
      icon={<AccountBalanceWalletIcon />}
      color="#4caf50"
      data={data}
    />
  );
}

function TradeHistorianNode({ data }) {
  return (
    <NodeCard
      title="Trade Historian"
      icon={<HistoryIcon />}
      color="#ff9800"
      data={data}
    />
  );
}

function ReportSynthesizerNode({ data }) {
  return (
    <NodeCard
      title="Report Synthesizer"
      icon={<AutoGraphIcon />}
      color="#9c27b0"
      data={data}
    />
  );
}

function ExecutionLayerNode({ data }) {
  return (
    <NodeCard
      title="Execution Layer"
      icon={<PlayCircleIcon />}
      color="#009688"
      data={data}
    />
  );
}

function DataSourceNode({ data }) {
  return (
    <NodeCard
      title="Data Source"
      icon={<DataUsageIcon />}
      color="#607d8b"
      data={data}
    />
  );
}

function ConditionNode({ data }) {
  return (
    <NodeCard
      title="Condition"
      icon={<CompareArrowsIcon />}
      color="#ff5722"
      data={data}
    />
  );
}

function MemoryNode({ data }) {
  const nodeData = {
    ...data,
    inputs: data.inputs || [
      { id: "data", label: "Data" },
      { id: "signals", label: "Signals" },
      { id: "trades", label: "Trades" },
    ],
    outputs: data.outputs || [
      { id: "history", label: "History" },
      { id: "patterns", label: "Patterns" },
      { id: "insights", label: "Insights" },
    ],
  };
  return (
    <NodeCard
      title="Memory"
      icon={<MemoryIcon />}
      color="#6a1b9a"
      data={nodeData}
    />
  );
}

function TelegramReportNode({ data }) {
  const nodeData = {
    ...data,
    inputs: data.inputs || [
      { id: "data", label: "Data" },
      { id: "signals", label: "Signals" },
      { id: "trades", label: "Trades" },
    ],
    outputs: data.outputs || [
      { id: "status", label: "Status" },
    ],
  };
  return (
    <NodeCard
      title="Telegram Reporter"
      icon={<TelegramIcon />}
      color="#0088cc"
      data={nodeData}
    />
  );
}

function SignalMatrixNode({ data }) {
  const nodeData = {
    ...data,
    inputs: data.inputs || [
      { id: "signals", label: "Signals" },
      { id: "market_data", label: "Market Data" },
      { id: "analysis", label: "Analysis" },
    ],
    outputs: data.outputs || [
      { id: "filtered_signals", label: "Filtered Signals" },
      { id: "auto_trades", label: "Auto Trades" },
      { id: "indicator_scores", label: "Indicator Scores" },
      { id: "schedule_status", label: "Schedule Status" },
    ],
  };
  return (
    <NodeCard
      title="Signal Matrix"
      icon={<InsightsIcon />}
      color="#00e676"
      data={nodeData}
    />
  );
}

function AIScheduleNode({ data }) {
  const [aiConfig, setAiConfig] = useState(null);

  useEffect(() => {
    const fetchAiSchedule = async () => {
      if (data.strategyId) {
        try {
          const res = await fetch(
            `${StrategyEngineService.baseUrl || ''}/api/admin/ai-schedule/${data.strategyId}`
          );
          if (res.ok) {
            const json = await res.json();
            if (json.success) setAiConfig(json.data);
          }
        } catch (e) { /* silent */ }
      }
    };
    fetchAiSchedule();
    const interval = setInterval(fetchAiSchedule, 60000);
    return () => clearInterval(interval);
  }, [data.strategyId]);

  const nodeData = {
    ...data,
    inputs: data.inputs || [
      { id: "signals", label: "Signals" },
      { id: "analysis", label: "Analysis" },
      { id: "market_data", label: "Market Data" },
    ],
    outputs: data.outputs || [
      { id: "filtered_signals", label: "Filtered Signals" },
      { id: "auto_trades", label: "Auto Trades" },
      { id: "ai_policy", label: "AI Policy" },
      { id: "schedule_status", label: "Schedule Status" },
    ],
    aiConfig,
  };
  return (
    <NodeCard
      title="AI Schedule"
      icon={<SmartToyIcon />}
      color="#7c4dff"
      data={nodeData}
    />
  );
}

function SchedulerNode({ data }) {
  const [nextExecution, setNextExecution] = useState(null);
  const [countdown, setCountdown] = useState(null);
  
  // Fetch schedule info on mount and periodically
  useEffect(() => {
    const fetchSchedule = async () => {
      if (data.strategyId) {
        try {
          const scheduleData = await StrategyEngineService.getSchedule(data.strategyId);
          if (scheduleData?.nextExecutionDisplay) {
            setNextExecution(scheduleData.nextExecutionDisplay);
          } else if (scheduleData?.nextRun) {
            const nextRunDate = new Date(scheduleData.nextRun);
            const timezone = scheduleData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
            const nextInTz = new Date(nextRunDate.toLocaleString('en-US', { timeZone: timezone }));
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            setNextExecution({
              time: `${String(nextInTz.getHours()).padStart(2,'0')}:${String(nextInTz.getMinutes()).padStart(2,'0')}`,
              day: dayNames[nextInTz.getDay()],
              timestamp: scheduleData.nextRun,
              mode: scheduleData.goldenHoursMode ? 'golden_hours' : 'standard'
            });
          }
        } catch (e) {
          // Silently fail
        }
      }
    };
    fetchSchedule();
    const interval = setInterval(fetchSchedule, 30000);
    return () => clearInterval(interval);
  }, [data.strategyId]);
  
  // Update countdown every second
  useEffect(() => {
    if (nextExecution?.timestamp) {
      const updateCountdown = () => {
        const now = new Date();
        const nextRun = new Date(nextExecution.timestamp);
        const diffMs = nextRun - now;
        if (diffMs <= 0) {
          setCountdown("Now");
          return;
        }
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        if (hours > 0) {
          setCountdown(`${hours}h ${minutes}m`);
        } else if (minutes > 0) {
          setCountdown(`${minutes}m ${seconds}s`);
        } else {
          setCountdown(`${seconds}s`);
        }
      };
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [nextExecution?.timestamp]);

  // Ensure inputs/outputs are set for Scheduler node
  const nodeData = {
    ...data,
    inputs: data.inputs || [
      { id: "signals", label: "Signals" },
      { id: "analysis", label: "Analysis" },
      { id: "market_data", label: "Market Data" },
    ],
    outputs: data.outputs || [
      { id: "filtered_signals", label: "Filtered Signals" },
      { id: "auto_trades", label: "Auto Trades" },
      { id: "schedule_status", label: "Schedule Status" },
      { id: "notifications", label: "Notifications" },
    ],
    // Pass next execution info to NodeCard
    nextExecution,
    countdown,
  };
  return (
    <NodeCard
      title="Scheduler"
      icon={<ScheduleIcon />}
      color="#e91e63"
      data={nodeData}
    />
  );
}

// Enhanced node card component with connection handles
function NodeCard({ title, icon, color, data }) {
  // Determine the active symbol for this node
  const getActiveSymbol = () => {
    // Check if node has inherited symbol from upstream
    if (data.inheritedSymbol) {
      return { symbol: data.inheritedSymbol, source: "inherited" };
    }
    // Check if Smart Symbol is enabled
    if (data.settings?.smartSymbolEnabled && data.settings?.activeSmartSymbol) {
      return { symbol: data.settings.activeSmartSymbol, source: "smart" };
    }
    // Check node's own symbol setting
    if (data.params?.symbol || data.settings?.symbol) {
      return {
        symbol: data.params?.symbol || data.settings?.symbol,
        source: "manual",
      };
    }
    return null;
  };

  // Determine the active broker for this node
  const getActiveBroker = () => {
    // Check if node has inherited broker from upstream
    if (data.inheritedBroker) {
      return { broker: data.inheritedBroker, source: "inherited" };
    }
    // Check node's own broker setting
    if (data.params?.broker || data.settings?.broker) {
      return {
        broker: data.params?.broker || data.settings?.broker,
        source: "manual",
      };
    }
    return null;
  };

  const activeSymbol = getActiveSymbol();
  const activeBroker = getActiveBroker();

  return (
    <Card
      sx={{ minWidth: 180, maxWidth: 280, boxShadow: 3, position: "relative" }}
    >
      {/* Input handles - appear on left side of node */}
      {data.inputs &&
        data.inputs.map((input, index) => {
          const inputId = typeof input === "object" ? input.id : input;
          const inputLabel = typeof input === "object" ? (input.label || input.id) : input;
          return (
          <Handle
            key={`input-${inputId}`}
            type="target"
            position={Position.Left}
            id={inputId}
            style={{
              top: `${20 + index * 20}px`,
              background: "#555",
              width: 8,
              height: 8,
              borderRadius: 4,
            }}
          >
            <Tooltip
              title={`Input: ${inputLabel}`}
              placement="left"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgba(0,0,0,0.8)",
                    "& .MuiTooltip-arrow": {
                      color: "rgba(0,0,0,0.8)",
                    },
                  },
                },
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: -30,
                  fontSize: "0.6rem",
                  fontWeight: "bold",
                  color: "#aaa",
                  width: 20,
                  textAlign: "right",
                }}
              >
                {inputLabel}
              </div>
            </Tooltip>
          </Handle>
        );
        })}


      {/* Output handles - appear on right side of node */}
      {data.outputs &&
        data.outputs.map((output, index) => {
          const outputId = typeof output === "object" ? output.id : output;
          const outputLabel = typeof output === "object" ? (output.label || output.id) : output;
          return (
          <Handle
            key={`output-${outputId}`}
            type="source"
            position={Position.Right}
            id={outputId}
            style={{
              top: `${20 + index * 20}px`,
              background: color,
              width: 8,
              height: 8,
              borderRadius: 4,
            }}
          >
            <Tooltip
              title={`Output: ${outputLabel}`}
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "rgba(0,0,0,0.8)",
                    "& .MuiTooltip-arrow": {
                      color: "rgba(0,0,0,0.8)",
                    },
                  },
                },
              }}
            >
              <div
                style={{
                  position: "absolute",
                  right: -30,
                  fontSize: "0.6rem",
                  fontWeight: "bold",
                  color: "#aaa",
                  width: 20,
                }}
              >
                {outputLabel}
              </div>
            </Tooltip>
          </Handle>
        );
        })}

      <CardContent sx={{ padding: 1, "&:last-child": { paddingBottom: 1 } }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              backgroundColor: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              width: 24,
              height: 24,
              mr: 1,
              color: "white",
              fontSize: "0.8rem",
            }}
          >
            {icon}
          </Box>
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ fontWeight: "bold" }}
          >
            {title}
          </Typography>

          <Tooltip title="Configure Node">
            <IconButton size="small" sx={{ ml: "auto" }}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Active Symbol Badge */}
        {activeSymbol && (
          <Tooltip
            title={
              activeSymbol.source === "inherited"
                ? "🔗 Symbol inherited from upstream node"
                : activeSymbol.source === "smart"
                  ? "🤖 Symbol from Smart Symbol Discovery"
                  : "✏️ Manually configured symbol"
            }
          >
            <Chip
              icon={
                activeSymbol.source === "inherited" ? (
                  <span>🔗</span>
                ) : activeSymbol.source === "smart" ? (
                  <span>🤖</span>
                ) : (
                  <span>✏️</span>
                )
              }
              label={activeSymbol.symbol}
              size="small"
              color={
                activeSymbol.source === "smart"
                  ? "success"
                  : activeSymbol.source === "inherited"
                    ? "info"
                    : "default"
              }
              sx={{
                mb: 0.5,
                mr: 0.5,
                fontWeight: "bold",
                "& .MuiChip-icon": { fontSize: "0.9rem" },
              }}
            />
          </Tooltip>
        )}

        {/* Active Broker Badge */}
        {activeBroker && (
          <Tooltip
            title={
              activeBroker.source === "inherited"
                ? "🔗 Broker inherited from upstream node"
                : "🏦 Manually configured broker"
            }
          >
            <Chip
              icon={
                activeBroker.source === "inherited" ? (
                  <span>🔗</span>
                ) : (
                  <span>🏦</span>
                )
              }
              label={activeBroker.broker}
              size="small"
              color={
                activeBroker.source === "inherited" ? "warning" : "secondary"
              }
              sx={{
                mb: 0.5,
                fontWeight: "bold",
                "& .MuiChip-icon": { fontSize: "0.9rem" },
              }}
            />
          </Tooltip>
        )}

        {/* Next Execution Display for Scheduler Node */}
        {data.nextExecution && (
          <Box 
            sx={{ 
              mt: 0.5, 
              mb: 0.5, 
              p: 0.75, 
              borderRadius: 1,
              background: data.nextExecution.mode === 'golden_hours' 
                ? 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)' 
                : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                  {data.nextExecution.mode === 'golden_hours' ? '⭐ Next Run' : '🔄 Next Run'}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {data.nextExecution.time} ({data.nextExecution.day})
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h6" fontWeight="bold">
                  {data.countdown || '--'}
                </Typography>
              </Box>
            </Box>
            {data.nextExecution.repeatNumber && data.nextExecution.totalRepeats && (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Repeat {data.nextExecution.repeatNumber}/{data.nextExecution.totalRepeats}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ mb: 1 }} />

        <Typography variant="body2" color="text.secondary">
          {data.description}
        </Typography>

        <Box sx={{ mt: 1 }}>
          {data.params &&
            Object.entries(data.params)
              .filter(([key]) => key !== "symbol") // Don't show symbol twice
              .map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5, fontSize: "0.7rem" }}
                />
              ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// SignalCard component with AI optimal entry analysis and position sizing
function SignalCard({ signal, idx, currentRunId, setRunDetails, positionSize = { usdValue: 100, budgetPercent: 5 }, onSizeChange }) {
  const [optimalEntry, setOptimalEntry] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showOrderDialog, setShowOrderDialog] = React.useState(false);
  const [showSizeEdit, setShowSizeEdit] = React.useState(false);
  
  // Ensure positionSize has valid defaults
  const safePositionSize = positionSize || { usdValue: 100, budgetPercent: 5 };
  const [localSize, setLocalSize] = React.useState(safePositionSize);

  // Update local size when prop changes
  React.useEffect(() => {
    if (positionSize) {
      setLocalSize(positionSize);
    }
  }, [positionSize]);

  // Fetch optimal entry analysis when signal loads
  React.useEffect(() => {
    const fetchOptimalEntry = async () => {
      try {
        setIsLoading(true);
        const result = await StrategyEngineService.getOptimalEntry(
          signal.symbol,
          signal.direction.toLowerCase()
        );
        setOptimalEntry(result);
      } catch (e) {
        console.error("Error fetching optimal entry:", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptimalEntry();
  }, [signal.symbol, signal.direction]);

  // Calculate quantity from USD value
  const calculateQuantity = () => {
    const price = signal.price || optimalEntry?.currentPrice || 1;
    return (safePositionSize.usdValue || 100) / price;
  };

  const handleMarketOrder = async () => {
    // Validate direction - only allow buy or sell
    const side = signal.direction?.toLowerCase();
    if (side !== 'buy' && side !== 'sell') {
      alert(`Cannot execute order: Invalid direction "${signal.direction}". Only BUY or SELL orders are allowed.`);
      return;
    }
    
    const quantity = calculateQuantity();
    try {
      const result = await StrategyEngineService.openPosition(currentRunId, {
        symbol: signal.symbol,
        side: side,
        quantity: quantity,
        price: signal.price,
        confidence: signal.confidence,
        orderType: 'market',
        usdValue: safePositionSize.usdValue,
        budgetPercent: safePositionSize.budgetPercent,
      });
      if (result?.success) {
        setRunDetails(prev => ({
          ...prev,
          positions: [...(prev.positions || []), result.data],
          signals: prev.signals.filter((_, i) => i !== idx),
        }));
      }
    } catch (e) {
      console.error("Error opening position:", e);
      alert("Error opening position: " + e.message);
    }
  };

  const handleLimitOrder = async () => {
    if (!optimalEntry?.optimalPrice) return;
    
    // Validate direction - only allow buy or sell
    const side = signal.direction?.toLowerCase();
    if (side !== 'buy' && side !== 'sell') {
      alert(`Cannot execute order: Invalid direction "${signal.direction}". Only BUY or SELL orders are allowed.`);
      return;
    }
    
    const quantity = calculateQuantity();
    
    try {
      const result = await StrategyEngineService.openPosition(currentRunId, {
        symbol: signal.symbol,
        side: side,
        quantity: quantity,
        price: optimalEntry.optimalPrice,
        confidence: signal.confidence,
        orderType: 'limit',
        targetPrice: optimalEntry.optimalPrice,
        usdValue: safePositionSize.usdValue,
        budgetPercent: safePositionSize.budgetPercent,
      });
      if (result?.success) {
        setRunDetails(prev => ({
          ...prev,
          positions: [...(prev.positions || []), { ...result.data, isLimitOrder: true, targetPrice: optimalEntry.optimalPrice }],
          signals: prev.signals.filter((_, i) => i !== idx),
        }));
        setShowOrderDialog(false);
      }
    } catch (e) {
      console.error("Error placing limit order:", e);
      alert("Error placing limit order: " + e.message);
    }
  };

  const handleSizeUpdate = () => {
    onSizeChange(idx, localSize);
    setShowSizeEdit(false);
  };

  const showLimitOrderButton = optimalEntry?.suggestLimitOrder && optimalEntry?.probability >= 70;

  return (
    <>
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          p: 1, 
          mb: 1, 
          bgcolor: signal.direction === "BUY" ? "rgba(46, 125, 50, 0.2)" : signal.direction === "SELL" ? "rgba(211, 47, 47, 0.2)" : "rgba(255,255,255,0.1)",
          border: '1px solid',
          borderColor: signal.direction === "BUY" ? "success.main" : signal.direction === "SELL" ? "error.main" : "grey.500",
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {signal.symbol}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              @ {signal.price ? `$${parseFloat(signal.price).toFixed(signal.price < 1 ? 8 : 2)}` : "Market"}
              {signal.confidence && ` • ${Math.round(signal.confidence * 100)}% conf`}
              {signal.method === 'price_action' && (
                <Chip 
                  label="📊 Price Action" 
                  size="small" 
                  sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: 'secondary.main', color: 'white' }} 
                />
              )}
            </Typography>
            {/* Price Action signals detail */}
            {signal.method === 'price_action' && signal.priceActionSignals && (
              <Typography variant="caption" color="secondary.light" sx={{ display: 'block', mt: 0.25 }}>
                {signal.trend && `Trend: ${signal.trend} `}
                {signal.priceActionSignals?.length > 0 && `• Patterns: ${signal.priceActionSignals.join(', ')}`}
                {signal.entryTimeframe && ` • Entry: ${signal.entryTimeframe}`}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Limit Order Button - only show if AI suggests better price with >70% probability */}
            {showLimitOrderButton && (
              <Tooltip 
                title={`AI suggests ${signal.direction === "BUY" ? "buying" : "selling"} at $${optimalEntry.optimalPrice} (${optimalEntry.probability}% probability, ${optimalEntry.timeEstimate})`}
                placement="top"
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  sx={{ minWidth: 60, fontSize: '0.7rem' }}
                  onClick={() => setShowOrderDialog(true)}
                >
                  <ReceiptIcon sx={{ fontSize: 14, mr: 0.5 }} /> ORDER
                </Button>
              </Tooltip>
            )}
            {/* Market Order Button */}
            <Button
              size="small"
              variant="contained"
              color={signal.direction === "BUY" ? "success" : "error"}
              sx={{ minWidth: 70 }}
              onClick={handleMarketOrder}
            >
              <FiberManualRecordIcon sx={{ fontSize: 12, mr: 0.5 }} /> {signal.direction === "BUY" ? "BUY" : "SELL"}
            </Button>
          </Box>
        </Box>
        
        {/* Position Size Display/Edit */}
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
          {!showSizeEdit ? (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                borderRadius: 0.5,
                p: 0.5,
                mx: -0.5,
              }}
              onClick={() => setShowSizeEdit(true)}
            >
              <Typography variant="caption" color="text.secondary">
                Size: <strong style={{ color: '#fff' }}>${(safePositionSize.usdValue || 100).toFixed(2)}</strong>
                {' '}({(safePositionSize.budgetPercent || 5).toFixed(1)}%)
                {' '}≈ {calculateQuantity().toFixed(signal.price < 1 ? 6 : 4)} units
              </Typography>
              <Typography variant="caption" color="primary.main" sx={{ ml: 1, display: 'flex', alignItems: 'center' }}><EditIcon sx={{ fontSize: 14, mr: 0.5 }} /> Edit</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="$ Value"
                  type="number"
                  value={localSize.usdValue}
                  onChange={(e) => setLocalSize(prev => ({ ...prev, usdValue: parseFloat(e.target.value) || 0 }))}
                  sx={{ width: 90 }}
                  InputProps={{ sx: { fontSize: '0.75rem' } }}
                  InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                />
                <TextField
                  size="small"
                  label="% Budget"
                  type="number"
                  value={localSize.budgetPercent}
                  onChange={(e) => setLocalSize(prev => ({ ...prev, budgetPercent: parseFloat(e.target.value) || 0 }))}
                  sx={{ width: 80 }}
                  InputProps={{ sx: { fontSize: '0.75rem' } }}
                  InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                />
                <Button size="small" variant="contained" onClick={handleSizeUpdate} sx={{ minWidth: 40, fontSize: '0.7rem' }}>
                  ✓
                </Button>
                <Button size="small" variant="text" onClick={() => setShowSizeEdit(false)} sx={{ minWidth: 30, fontSize: '0.7rem' }}>
                  ✕
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        
        {/* AI Analysis Info */}
        {!isLoading && optimalEntry && (
          <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px dashed rgba(255,255,255,0.2)' }}>
            {showLimitOrderButton ? (
              <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LightbulbIcon sx={{ fontSize: 14, mr: 0.5 }} /> Better price: ${optimalEntry.optimalPrice} ({optimalEntry.priceDifferencePercent > 0 ? '+' : ''}{optimalEntry.priceDifferencePercent}%) • {optimalEntry.probability}% likely • {optimalEntry.timeEstimate}
              </Typography>
            ) : (
              <Typography variant="caption" color="success.light" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                ✓ Current price is optimal for {signal.direction.toLowerCase()}ing
              </Typography>
            )}
          </Box>
        )}
        {isLoading && (
          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={12} />
            <Typography variant="caption" color="text.secondary">Analyzing optimal entry...</Typography>
          </Box>
        )}
      </Box>

      {/* Limit Order Dialog */}
      <Dialog open={showOrderDialog} onClose={() => setShowOrderDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <ReceiptIcon sx={{ mr: 1 }} /> Place Limit Order
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>{signal.symbol}</strong> - {signal.direction}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Current Price:</Typography>
              <Typography variant="caption">${optimalEntry?.currentPrice?.toFixed(optimalEntry?.currentPrice < 1 ? 8 : 2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Limit Price:</Typography>
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                ${optimalEntry?.optimalPrice?.toFixed(optimalEntry?.optimalPrice < 1 ? 8 : 2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Price Difference:</Typography>
              <Typography variant="caption" color={optimalEntry?.priceDifferencePercent < 0 ? "success.main" : "error.main"}>
                {optimalEntry?.priceDifferencePercent > 0 ? '+' : ''}{optimalEntry?.priceDifferencePercent}%
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Probability:</Typography>
              <Typography variant="caption" color="warning.main">{optimalEntry?.probability}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Est. Time:</Typography>
              <Typography variant="caption">{optimalEntry?.timeEstimate}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
              <Typography variant="caption">
                {optimalEntry?.reasons?.[0] || "AI analysis suggests waiting for better entry"}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOrderDialog(false)} size="small">Cancel</Button>
          <Button 
            onClick={handleLimitOrder} 
            variant="contained" 
            color="warning"
            size="small"
          >
            Place Limit Order
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Initial nodes for a new strategy
const initialNodes = [
  {
    id: "0",
    position: { x: 50, y: 150 },
    data: {
      description: "Fetches market data",
      params: {
        symbol: "BTC/USD",
        timeframe: "1h",
      },
      inputs: [],
      outputs: ["market_data"],
    },
    type: "dataSource",
  },
  {
    id: "1",
    position: { x: 300, y: 100 },
    data: {
      description: "Analyzes technical indicators",
      params: {
        symbol: "BTC/USD",
        timeframe: "1d",
        indicators: "RSI, MACD",
      },
      inputs: ["market_data"],
      outputs: ["signals", "analysis"],
    },
    type: "technicalAnalyst",
  },
  {
    id: "2",
    position: { x: 550, y: 100 },
    data: {
      description: "Manages risk parameters",
      params: {
        maxRiskPercent: "2",
        maxPositionPercent: "10",
      },
      inputs: ["signals", "market_data"],
      outputs: ["approved_orders", "risk_report"],
    },
    type: "riskManager",
  },
  {
    id: "3",
    position: { x: 400, y: 300 },
    data: {
      description: "Executes the trade",
      params: {
        mode: "Auto",
        confirmRequired: "Yes",
      },
      inputs: ["approved_orders", "decisions"],
      outputs: ["trades", "execution_status"],
    },
    type: "executionLayer",
  },
  {
    id: "4",
    position: { x: 650, y: 300 },
    data: {
      description: "Stores trade information",
      params: {
        capacity: "100",
        persistenceMode: "database",
      },
      inputs: ["data"],
      outputs: ["history"],
    },
    type: "memory",
  },
];

// Connection styles are applied directly in ReactFlow component

// Available node templates for the sidebar
const nodeTemplates = [
  {
    type: "technicalAnalyst",
    label: "Technical Analyst",
    icon: <ShowChartIcon />,
    description: "Multi-timeframe analysis with pattern detection and indicators",
    color: "#3f51b5",
    defaultParams: {
      symbol: "BTC/USD",
      timeframe: "1d",
      indicators: "RSI, MACD, MA, EMA, BB, ATR",
      // Multi-timeframe analysis
      multiTimeframe: "Yes",
      timeframes: "15m,1h,4h,1d",
      confluenceRequired: "2",
      // Pattern detection
      detectPatterns: "Yes",
      patterns: "Head & Shoulders, Double Top/Bottom, Triangle, Wedge",
      // Signal generation
      signalStrength: "medium",
      minConfidence: "60",
    },
    inputs: ["market_data"],
    outputs: ["signals", "analysis", "patterns", "confluence"],
  },
  {
    type: "fundamentalAnalyst",
    label: "Fundamental Analyst",
    icon: <ArticleIcon />,
    description: "News impact, market sentiment analysis",
    color: "#f50057",
    defaultParams: {
      sources: "News API, Twitter",
      sentimentThreshold: "0.6",
      topics: "crypto, blockchain",
    },
    inputs: ["market_data"],
    outputs: ["signals", "sentiment"],
  },
  {
    type: "riskManager",
    label: "Risk Manager",
    icon: <AccountBalanceWalletIcon />,
    description: "Portfolio-level risk, TP/SL, position sizing, and exposure tracking",
    color: "#4caf50",
    defaultParams: {
      maxRiskPercent: "2",
      maxPositionPercent: "10",
      stopLossType: "ATR",
      tradingMode: "spot",
      leverage: "1",
      takeProfitPercent: "3",
      stopLossPercent: "2",
      autoAdjustForLeverage: "Yes",
      // Portfolio-level risk
      maxPortfolioRisk: "10",
      maxOpenPositions: "5",
      maxExposurePerAsset: "20",
      maxDrawdownPercent: "15",
      correlationCheck: "Yes",
      // Position sizing
      positionSizingMethod: "fixed_percent",
      kellyFraction: "0.25",
      // Risk alerts
      alertOnHighExposure: "Yes",
      alertOnDrawdown: "Yes",
      pauseOnMaxDrawdown: "Yes",
    },
    inputs: ["signals", "market_data", "portfolio"],
    outputs: ["approved_orders", "risk_report", "exposure_report"],
  },
  {
    type: "tradeHistorian",
    label: "Trade Historian",
    icon: <HistoryIcon />,
    description: "Historical pattern validation",
    color: "#ff9800",
    defaultParams: {
      lookbackPeriod: "30",
      analysisMode: "Comparison",
    },
    inputs: ["trades", "signals"],
    outputs: ["patterns", "history"],
  },
  {
    type: "reportSynthesizer",
    label: "Report Synthesizer",
    icon: <AutoGraphIcon />,
    description: "Integrates other agents, resolves conflicts",
    color: "#9c27b0",
    defaultParams: {
      conflictResolution: "Weighted",
      reportFormat: "Detailed",
    },
    inputs: ["analysis", "patterns", "risk_report", "signals"],
    outputs: ["decisions", "report"],
  },
  {
    type: "executionLayer",
    label: "Execution Layer",
    icon: <PlayCircleIcon />,
    description: "Execute trades with multiple order types, TP/SL, and smart routing",
    color: "#009688",
    defaultParams: {
      broker: "kucoin",
      tradingMode: "spot",
      leverage: "1",
      mode: "Auto",
      confirmRequired: "Yes",
      delayMs: "500",
      paperTrading: "Yes",
      takeProfitPercent: "3",
      stopLossPercent: "2",
      // Order types
      orderType: "market",
      limitOrderOffset: "0.1",
      useStopLimit: "No",
      stopLimitOffset: "0.05",
      // Trailing stop
      useTrailingStop: "No",
      trailingStopPercent: "1",
      trailingStopActivation: "1",
      // Smart execution
      splitLargeOrders: "Yes",
      maxOrderSize: "10000",
      slippageTolerance: "0.5",
      // Iceberg orders
      useIceberg: "No",
      icebergVisiblePercent: "20",
    },
    inputs: ["approved_orders", "decisions"],
    outputs: ["trades", "execution_status", "order_book"],
  },
  {
    type: "dataSource",
    label: "Data Source",
    icon: <DataUsageIcon />,
    description: "Market data with symbol rotation and multi-symbol support",
    color: "#607d8b",
    defaultParams: {
      broker: "kucoin",
      tradingMode: "spot",
      symbol: "BTC/USDT",
      timeframe: "1h",
      cache: "Yes",
      refresh: "1m",
      // Symbol rotation
      symbolRotation: "No",
      symbols: "BTC/USDT,ETH/USDT,SOL/USDT,XRP/USDT",
      rotationInterval: "1h",
      rotationMode: "sequential",
      // Multi-symbol
      multiSymbol: "No",
      maxConcurrentSymbols: "5",
      // Filtering
      minVolume24h: "1000000",
      minMarketCap: "0",
      excludeStablecoins: "Yes",
    },
    inputs: [],
    outputs: ["market_data", "symbol_list", "rotation_status"],
  },
  {
    type: "condition",
    label: "Condition",
    icon: <CompareArrowsIcon />,
    description: "Complex AND/OR logic with multiple conditions and nested rules",
    color: "#ff5722",
    defaultParams: {
      condition: "RSI > 70",
      trueRoute: "Sell",
      falseRoute: "Hold",
      // Complex logic
      logicMode: "simple",
      conditions: "RSI > 70 AND MACD_SIGNAL < 0",
      operator: "AND",
      // Nested conditions
      nestedConditions: "",
      // Time-based
      timeCondition: "No",
      activeHours: "09:00-17:00",
      activeDays: "Mon,Tue,Wed,Thu,Fri",
      // Value comparisons
      compareIndicators: "No",
      indicator1: "RSI",
      comparison: ">",
      indicator2: "50",
      // Threshold alerts
      alertOnTrigger: "No",
    },
    inputs: ["signals", "market_data", "sentiment", "analysis"],
    outputs: ["filtered_signals", "true_branch", "false_branch", "condition_log"],
  },
  {
    type: "memory",
    label: "Memory",
    icon: <MemoryIcon />,
    description: "Pattern recognition, historical analysis, and decision memory",
    color: "#6a1b9a",
    defaultParams: {
      capacity: "100",
      persistenceMode: "database",
      // Pattern recognition
      patternRecognition: "Yes",
      patternTypes: "price,volume,indicator",
      minPatternOccurrences: "3",
      patternConfidenceThreshold: "70",
      // Historical analysis
      lookbackPeriods: "7,30,90",
      compareWithHistory: "Yes",
      similarityThreshold: "80",
      // Decision memory
      rememberDecisions: "Yes",
      decisionRetention: "90",
      learnFromOutcomes: "Yes",
      // Alerts
      alertOnPatternMatch: "Yes",
      alertOnAnomaly: "Yes",
    },
    inputs: [{ id: "data", label: "Data" }, { id: "signals", label: "Signals" }, { id: "trades", label: "Trades" }],
    outputs: [{ id: "history", label: "History" }, { id: "patterns", label: "Patterns" }, { id: "insights", label: "Insights" }],
  },
  {
    type: "telegramReport",
    label: "Telegram Reporter",
    icon: <TelegramIcon />,
    description: "Send trading reports and alerts via Telegram",
    color: "#0088cc",
    defaultParams: {
      botToken: "",
      chatId: "",
      frequency: "daily",
      includeCharts: true,
      includeSummary: true,
      alertOnTrades: true,
    },
    inputs: [
      { id: "data", label: "Data" },
      { id: "signals", label: "Signals" },
      { id: "trades", label: "Trades" },
    ],
    outputs: [{ id: "status", label: "Status" }],
  },
  {
    type: "scheduler",
    label: "Scheduler",
    icon: <ScheduleIcon />,
    description: "Automated scheduling with confidence threshold, TP/SL, cooldown, and notifications",
    color: "#e91e63",
    defaultParams: {
      broker: "kucoin",
      enabled: "Yes",
      interval: "5m",
      confidenceThreshold: "82",
      autoExecute: "No",
      maxSignalsPerRun: "5",
      tradingHoursStart: "00:00",
      tradingHoursEnd: "23:59",
      daysActive: "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
      paperTrading: "Yes",
      positionSizeUsd: "100",
      positionSizePercent: "2.5",
      // Stop Loss / Take Profit — AI-recommended scalp preset (v3.2.1)
      stopLossPercent: "1.5",
      takeProfitPercent: "2.5",
      useTrailingStop: "Yes",
      trailingStopPercent: "0.6",
      trailingStopAiMode: "No",
      // Trailing Benefit — fast profit locking
      useTrailingBenefit: "Yes",
      trailingBenefitActivation: "1",
      trailingBenefitLockPercent: "50",
      trailingBenefitAiMode: "No",
      // Cooldown — 3 min per symbol for scalps
      cooldownMinutes: "3",
      cooldownPerSymbol: "Yes",
      // Retry Logic
      retryOnFailure: "Yes",
      maxRetries: "3",
      retryDelaySeconds: "60",
      // Notifications
      notifyOnTrade: "Yes",
      notifyOnError: "Yes",
      telegramEnabled: "No",
      // Dry Run
      dryRunMode: "No",
    },
    inputs: [
      { id: "signals", label: "Signals" },
      { id: "analysis", label: "Analysis" },
      { id: "market_data", label: "Market Data" },
    ],
    outputs: [
      { id: "filtered_signals", label: "Filtered Signals" },
      { id: "auto_trades", label: "Auto Trades" },
      { id: "schedule_status", label: "Schedule Status" },
      { id: "notifications", label: "Notifications" },
    ],
  },
  {
    type: "aiSchedule",
    label: "AI Schedule (Agentic)",
    icon: <SmartToyIcon />,
    description: "AI-managed schedule: auto mode selection, policy tuning, daily optimization, experiments",
    color: "#7c4dff",
    defaultParams: {
      aiModeEnabled: true,
      experimentEnabled: false,
      tradeWeekends: false,
      workingDays: "Mon,Tue,Wed,Thu,Fri",
      rotationPolicy: "round_robin",
      maxDailyLossPct: 3,
      maxDrawdownPct: 10,
      maxConcurrentPositions: 5,
    },
    inputs: [
      { id: "signals", label: "Signals" },
      { id: "analysis", label: "Analysis" },
      { id: "market_data", label: "Market Data" },
    ],
    outputs: [
      { id: "filtered_signals", label: "Filtered Signals" },
      { id: "auto_trades", label: "Auto Trades" },
      { id: "ai_policy", label: "AI Policy" },
      { id: "schedule_status", label: "Schedule Status" },
    ],
  },
  {
    type: "signalMatrix",
    label: "Signal Matrix",
    icon: <InsightsIcon />,
    description: "Modular indicator stack with weighted scoring, multi-timeframe analysis, and quality metrics",
    color: "#00e676",
    defaultParams: {
      broker: "kucoin",
      enabled: "Yes",
      interval: "5m",
      confidenceThreshold: "82",
      autoExecute: "No",
      maxSignalsPerRun: "5",
      paperTrading: "Yes",
      positionSizeUsd: "100",
      tradingMode: "futures",
      leverage: "5",
      stopLossPercent: "1.0",
      takeProfitPercent: "1.2",
      useTrailingStop: "Yes",
      trailingStopPercent: "0.3",
      cooldownMinutes: "3",
      cooldownPerSymbol: "Yes",
      indicatorStack: {
        enabled: true,
        combineMode: "WEIGHTED_SCORE",
        minScoreToTrade: 30,
        allowTradeIfMissingData: false,
        consensusMinAgree: 2,
        indicators: [],
      },
    },
    inputs: [
      { id: "signals", label: "Signals" },
      { id: "market_data", label: "Market Data" },
      { id: "analysis", label: "Analysis" },
    ],
    outputs: [
      { id: "filtered_signals", label: "Filtered Signals" },
      { id: "auto_trades", label: "Auto Trades" },
      { id: "indicator_scores", label: "Indicator Scores" },
      { id: "schedule_status", label: "Schedule Status" },
    ],
  },
];

// Strategy Templates
const strategyTemplates = {
  blank: {
    name: "Blank Strategy",
    description: "Start with an empty canvas",
    nodes: [],
    edges: [],
    category: "Basic",
  },
  reporting: {
    name: "Reporting Strategy",
    description: "Automated strategy with Telegram reporting integration",
    category: "Reporting",
    nodes: [
      {
        id: "1",
        type: "dataSource",
        position: { x: 50, y: 100 },
        data: {
          description: "Market data feed",
          params: { symbol: "BTC/USD", timeframe: "1h" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "2",
        type: "technicalAnalyst",
        position: { x: 350, y: 50 },
        data: {
          description: "Technical analysis for BTC/USD",
          params: { timeframe: "1h", indicators: "RSI,MACD" },
          inputs: [],
          outputs: ["signals"],
        },
      },
      {
        id: "3",
        type: "memory",
        position: { x: 350, y: 180 },
        data: {
          description: "Historical data storage",
          params: { capacity: "500", persistenceMode: "database" },
          inputs: ["data"],
          outputs: ["history"],
        },
      },
      {
        id: "4",
        type: "telegramReport",
        position: { x: 650, y: 100 },
        data: {
          description: "Telegram reporting service",
          params: {
            frequency: "daily",
            includeCharts: true,
            includeSummary: true,
            alertOnTrades: true,
          },
          inputs: ["data", "signals", "trades"],
          outputs: ["status"],
        },
      },
    ],
    edges: [
      {
        id: "e1-2",
        source: "1",
        target: "2",
        sourceHandle: "market_data",
        targetHandle: "data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e1-3",
        source: "1",
        target: "3",
        sourceHandle: "market_data",
        targetHandle: "data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2-4",
        source: "2",
        target: "4",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3-4",
        source: "3",
        target: "4",
        sourceHandle: "history",
        targetHandle: "data",
        animated: true,
        type: "smoothstep",
      },
    ],
  },
  technical: {
    name: "Technical Analysis Strategy",
    description: "Price action based strategy using technical indicators",
    nodes: [
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 100, y: 150 },
        data: {
          description: "Analyzes technical indicators",
          params: {
            symbol: "BTC/USD",
            timeframe: "1d",
            indicators: "RSI, MACD, MA",
          },
          inputs: [],
          outputs: ["signals"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 400, y: 150 },
        data: {
          description: "Manages risk parameters",
          params: { maxRisk: "2%", leverage: "1x", stopLossType: "ATR" },
          inputs: ["signals"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 700, y: 150 },
        data: {
          description: "Executes the trade",
          params: { mode: "Auto", confirmRequired: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "mem_1",
        type: "memory",
        position: { x: 700, y: 300 },
        data: {
          description: "Stores trade information",
          params: { storageType: "Local", retention: "90 days" },
          inputs: ["trades"],
          outputs: [],
        },
      },
    ],
    edges: [
      {
        id: "e-tech-risk",
        source: "tech_1",
        target: "risk_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
        label: "Signals",
      },
      {
        id: "e-risk-exec",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
        label: "Orders",
      },
      {
        id: "e-exec-mem",
        source: "exec_1",
        target: "mem_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
        label: "Trades",
      },
    ],
  },
  fundamental: {
    name: "Fundamental Analysis Strategy",
    description: "News and sentiment based trading strategy",
    nodes: [
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 100, y: 150 },
        data: {
          description: "Analyzes news and sentiment",
          params: { sources: "News API, Twitter", sentimentThreshold: "0.7" },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 400, y: 150 },
        data: {
          description: "Manages risk parameters",
          params: { maxRisk: "1.5%", leverage: "1x", stopLossType: "Fixed" },
          inputs: ["sentiment"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 700, y: 150 },
        data: {
          description: "Executes the trade",
          params: { mode: "Manual", confirmRequired: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "mem_1",
        type: "memory",
        position: { x: 400, y: 300 },
        data: {
          description: "Stores trade information",
          params: { storageType: "Local", retention: "90 days" },
          inputs: ["trades"],
          outputs: [],
        },
      },
    ],
    edges: [
      {
        id: "e-fund-risk",
        source: "fund_1",
        target: "risk_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
        label: "Sentiment",
      },
      {
        id: "e-risk-exec",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
        label: "Orders",
      },
      {
        id: "e-exec-mem",
        source: "exec_1",
        target: "mem_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
        label: "Trades",
      },
    ],
  },
  hybrid: {
    name: "Hybrid Analysis Strategy",
    description: "Combined technical and fundamental analysis",
    nodes: [
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 100, y: 100 },
        data: {
          description: "Analyzes technical indicators",
          params: {
            symbol: "BTC/USD",
            timeframe: "1d",
            indicators: "RSI, MACD, MA",
          },
          inputs: [],
          outputs: ["signals"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 100, y: 250 },
        data: {
          description: "Analyzes news and sentiment",
          params: { sources: "News API, Twitter", sentimentThreshold: "0.7" },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "synth_1",
        type: "reportSynthesizer",
        position: { x: 350, y: 175 },
        data: {
          description: "Combines multiple signals",
          params: { conflictResolution: "Weighted", reportFormat: "Detailed" },
          inputs: ["signals", "sentiment"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 600, y: 175 },
        data: {
          description: "Manages risk parameters",
          params: { maxRisk: "2%", leverage: "1x", stopLossType: "ATR" },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 850, y: 175 },
        data: {
          description: "Executes the trade",
          params: { mode: "Auto", confirmRequired: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e-tech-synth",
        source: "tech_1",
        target: "synth_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
        label: "Signals",
      },
      {
        id: "e-fund-synth",
        source: "fund_1",
        target: "synth_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
        label: "Sentiment",
      },
      {
        id: "e-synth-risk",
        source: "synth_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
        label: "Decisions",
      },
      {
        id: "e-risk-exec",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
        label: "Orders",
      },
    ],
  },

  // ============================================================================
  // CRYPTO TEMPLATES
  // ============================================================================

  cryptoScalping: {
    name: "Crypto Scalping Strategy",
    description: "High-frequency scalping for crypto markets with tight stops",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Real-time crypto price feed",
          params: { symbol: "BTC/USDT", timeframe: "1m", broker: "binance" },
          settings: { broker: "binance", symbol: "BTC/USDT", timeframe: "1m" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "Scalping indicators (EMA, RSI, Volume)",
          params: { indicators: "EMA9,EMA21,RSI,VWAP", timeframe: "1m" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "mtf_1",
        type: "multiTimeframe",
        position: { x: 300, y: 200 },
        data: {
          description: "Multi-timeframe confirmation",
          params: { timeframes: "1m,5m,15m", confirmationRequired: "2" },
          inputs: ["market_data"],
          outputs: ["mtf_signals"],
        },
      },
      {
        id: "agg_1",
        type: "aggregator",
        position: { x: 550, y: 125 },
        data: {
          description: "Combine scalping signals",
          params: { mode: "Unanimous", minConfidence: "0.8" },
          inputs: ["signals", "mtf_signals"],
          outputs: ["combined"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 125 },
        data: {
          description: "Tight risk management for scalping",
          params: {
            maxRisk: "0.5%",
            leverage: "3x",
            stopLossType: "Fixed",
            stopLoss: "0.3%",
          },
          inputs: ["combined"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1050, y: 125 },
        data: {
          description: "Fast execution on Binance",
          params: { mode: "Auto", confirmRequired: "No", broker: "binance" },
          settings: { broker: "binance", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "mtf_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "tech_1",
        target: "agg_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "mtf_1",
        target: "agg_1",
        sourceHandle: "mtf_signals",
        targetHandle: "mtf_signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "agg_1",
        target: "risk_1",
        sourceHandle: "combined",
        targetHandle: "combined",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  cryptoWhaleTracker: {
    name: "Crypto Whale Tracker",
    description:
      "Track large wallet movements and whale activity for trading signals",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Crypto market data",
          params: { symbol: "ETH/USDT", timeframe: "15m", broker: "binance" },
          settings: { broker: "binance", symbol: "ETH/USDT" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "On-chain whale tracking",
          params: { sources: "Whale Alert, Glassnode, Nansen" },
          settings: {
            cryptoAnalyticsConnections: { glassnode: true, nansen: true },
            socialMediaConnections: { twitter: true },
          },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "sent_1",
        type: "sentimentAnalyst",
        position: { x: 300, y: 250 },
        data: {
          description: "Social sentiment analysis",
          params: { sources: "Twitter, Reddit, Telegram" },
          inputs: [],
          outputs: ["sentiment_score"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 550, y: 150 },
        data: {
          description: "AI-powered whale movement analysis",
          params: {
            model: "GPT-4",
            strategy: "Whale Following",
            confidence: "0.75",
          },
          inputs: ["market_data", "sentiment", "sentiment_score"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 150 },
        data: {
          description: "Conservative risk for whale trades",
          params: {
            maxRisk: "2%",
            leverage: "2x",
            stopLossType: "Trailing",
            trailingStop: "3%",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1050, y: 150 },
        data: {
          description: "Execute whale-following trades",
          params: { mode: "Auto", confirmRequired: "Yes" },
          settings: { broker: "binance", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "tg_1",
        type: "telegramReport",
        position: { x: 1050, y: 300 },
        data: {
          description: "Whale alert notifications",
          params: {
            frequency: "realtime",
            alertOnTrades: true,
            includeCharts: true,
          },
          inputs: ["trades", "sentiment"],
          outputs: ["status"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "ai_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "fund_1",
        target: "ai_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "sent_1",
        target: "ai_1",
        sourceHandle: "sentiment_score",
        targetHandle: "sentiment_score",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "exec_1",
        target: "tg_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e7",
        source: "fund_1",
        target: "tg_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  defiYieldFarming: {
    name: "DeFi Yield Optimizer",
    description: "Automated yield farming strategy across DeFi protocols",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 100 },
        data: {
          description: "DeFi protocol data",
          params: { symbol: "ETH/USDT", timeframe: "1h", broker: "uniswap" },
          settings: { broker: "uniswap" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 50, y: 250 },
        data: {
          description: "DeFi protocol analysis",
          params: { sources: "DeFiLlama, DeBank" },
          settings: { cryptoAnalyticsConnections: { defillama: true } },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "cond_1",
        type: "condition",
        position: { x: 300, y: 100 },
        data: {
          description: "Check APY thresholds",
          params: { condition: "APY > 10% AND TVL > $1M", action: "proceed" },
          inputs: ["market_data"],
          outputs: ["filtered"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 550, y: 175 },
        data: {
          description: "AI yield optimization",
          params: {
            model: "GPT-4",
            strategy: "Yield Maximization",
            riskTolerance: "medium",
          },
          inputs: ["filtered", "sentiment"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 175 },
        data: {
          description: "DeFi risk management",
          params: {
            maxRisk: "5%",
            leverage: "1x",
            impermanentLossLimit: "10%",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1050, y: 175 },
        data: {
          description: "DeFi protocol interaction",
          params: { mode: "Auto", confirmRequired: "Yes" },
          settings: { broker: "uniswap", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "cond_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "cond_1",
        target: "ai_1",
        sourceHandle: "filtered",
        targetHandle: "filtered",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "fund_1",
        target: "ai_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  // ============================================================================
  // STOCK TEMPLATES
  // ============================================================================

  stockMomentum: {
    name: "Stock Momentum Strategy",
    description: "Momentum-based stock trading with earnings awareness",
    category: "Stocks",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Stock market data",
          params: { symbol: "AAPL", timeframe: "1d", broker: "alpaca" },
          settings: { broker: "alpaca", symbol: "AAPL" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "Momentum indicators",
          params: { indicators: "RSI,MACD,ADX,ROC", timeframe: "1d" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 250 },
        data: {
          description: "Earnings & fundamentals",
          params: { sources: "Alpha Vantage, Yahoo Finance" },
          settings: {
            marketDataConnections: { alphavantage: true, yahoo: true },
          },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "cond_1",
        type: "condition",
        position: { x: 550, y: 50 },
        data: {
          description: "Momentum filter",
          params: { condition: "RSI > 50 AND ADX > 25", action: "proceed" },
          inputs: ["signals"],
          outputs: ["filtered"],
        },
      },
      {
        id: "agg_1",
        type: "aggregator",
        position: { x: 750, y: 150 },
        data: {
          description: "Combine momentum & fundamentals",
          params: { mode: "Weighted", weights: "tech:0.6,fund:0.4" },
          inputs: ["filtered", "sentiment"],
          outputs: ["combined"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 1000, y: 150 },
        data: {
          description: "Stock risk management",
          params: {
            maxRisk: "2%",
            leverage: "1x",
            stopLossType: "ATR",
            atrMultiplier: "2",
          },
          inputs: ["combined"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1250, y: 150 },
        data: {
          description: "Execute on Alpaca",
          params: { mode: "Auto", confirmRequired: "Yes" },
          settings: { broker: "alpaca", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "tech_1",
        target: "cond_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "cond_1",
        target: "agg_1",
        sourceHandle: "filtered",
        targetHandle: "filtered",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "fund_1",
        target: "agg_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "agg_1",
        target: "risk_1",
        sourceHandle: "combined",
        targetHandle: "combined",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  stockDividend: {
    name: "Dividend Growth Strategy",
    description: "Long-term dividend stock investing with reinvestment",
    category: "Stocks",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Dividend stock data",
          params: {
            symbol: "JNJ",
            timeframe: "1w",
            broker: "interactive_brokers",
          },
          settings: { broker: "interactive_brokers", symbol: "JNJ" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 100 },
        data: {
          description: "Dividend analysis",
          params: {
            sources: "Dividend.com, Seeking Alpha",
            metrics: "Dividend Yield, Payout Ratio, Growth Rate",
          },
          settings: { marketDataConnections: { seekingalpha: true } },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "filter_1",
        type: "filter",
        position: { x: 550, y: 100 },
        data: {
          description: "Dividend aristocrat filter",
          params: {
            criteria:
              "Yield > 2.5% AND PayoutRatio < 60% AND DividendGrowth > 5yr",
          },
          inputs: ["sentiment"],
          outputs: ["filtered"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 800, y: 150 },
        data: {
          description: "AI dividend stock selection",
          params: {
            model: "GPT-4",
            strategy: "Dividend Growth",
            horizon: "long-term",
          },
          inputs: ["market_data", "filtered"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 1050, y: 150 },
        data: {
          description: "Conservative position sizing",
          params: {
            maxRisk: "3%",
            leverage: "1x",
            positionSize: "equal-weight",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1300, y: 150 },
        data: {
          description: "Execute on Interactive Brokers",
          params: { mode: "Manual", confirmRequired: "Yes" },
          settings: { broker: "interactive_brokers", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "ai_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "fund_1",
        target: "filter_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "filter_1",
        target: "ai_1",
        sourceHandle: "filtered",
        targetHandle: "filtered",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  stockEarningsPlay: {
    name: "Earnings Momentum Strategy",
    description: "Trade stocks around earnings announcements",
    category: "Stocks",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Stock price data",
          params: { symbol: "NVDA", timeframe: "1h", broker: "alpaca" },
          settings: { broker: "alpaca", symbol: "NVDA" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "Earnings calendar & estimates",
          params: { sources: "Earnings Whispers, Zacks" },
          settings: { marketDataConnections: { zacks: true } },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "sent_1",
        type: "sentimentAnalyst",
        position: { x: 300, y: 250 },
        data: {
          description: "Pre-earnings sentiment",
          params: { sources: "StockTwits, Twitter, Reddit" },
          inputs: [],
          outputs: ["sentiment_score"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 550, y: 150 },
        data: {
          description: "Pre-earnings technicals",
          params: { indicators: "IV,HV,ATR,Volume", timeframe: "1h" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 800, y: 150 },
        data: {
          description: "AI earnings prediction",
          params: {
            model: "GPT-4",
            strategy: "Earnings Momentum",
            confidence: "0.7",
          },
          inputs: ["signals", "sentiment", "sentiment_score"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 1050, y: 150 },
        data: {
          description: "Earnings play risk",
          params: {
            maxRisk: "1.5%",
            leverage: "1x",
            stopLossType: "Fixed",
            stopLoss: "5%",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1300, y: 150 },
        data: {
          description: "Execute earnings trade",
          params: { mode: "Auto", confirmRequired: "Yes" },
          settings: { broker: "alpaca", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "tech_1",
        target: "ai_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "fund_1",
        target: "ai_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "sent_1",
        target: "ai_1",
        sourceHandle: "sentiment_score",
        targetHandle: "sentiment_score",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  // ============================================================================
  // FOREX TEMPLATES
  // ============================================================================

  forexTrend: {
    name: "Forex Trend Following",
    description: "Classic trend following strategy for major currency pairs",
    category: "Forex",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Forex price feed",
          params: { symbol: "EUR/USD", timeframe: "4h", broker: "oanda" },
          settings: { broker: "oanda", symbol: "EUR/USD" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 100 },
        data: {
          description: "Trend indicators",
          params: { indicators: "EMA50,EMA200,ADX,Ichimoku", timeframe: "4h" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "mtf_1",
        type: "multiTimeframe",
        position: { x: 300, y: 250 },
        data: {
          description: "Multi-timeframe trend confirmation",
          params: { timeframes: "1h,4h,1d", alignment: "required" },
          inputs: ["market_data"],
          outputs: ["mtf_signals"],
        },
      },
      {
        id: "agg_1",
        type: "aggregator",
        position: { x: 550, y: 175 },
        data: {
          description: "Trend signal aggregation",
          params: { mode: "Unanimous", minConfidence: "0.75" },
          inputs: ["signals", "mtf_signals"],
          outputs: ["combined"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 175 },
        data: {
          description: "Forex risk management",
          params: {
            maxRisk: "1%",
            leverage: "10x",
            stopLossType: "ATR",
            atrMultiplier: "1.5",
          },
          inputs: ["combined"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1050, y: 175 },
        data: {
          description: "Execute on OANDA",
          params: { mode: "Auto", confirmRequired: "No" },
          settings: { broker: "oanda", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "mtf_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "tech_1",
        target: "agg_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "mtf_1",
        target: "agg_1",
        sourceHandle: "mtf_signals",
        targetHandle: "mtf_signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "agg_1",
        target: "risk_1",
        sourceHandle: "combined",
        targetHandle: "combined",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  forexNewsTrading: {
    name: "Forex News Trading",
    description: "Trade forex around major economic news releases",
    category: "Forex",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "Forex price feed",
          params: { symbol: "GBP/USD", timeframe: "5m", broker: "oanda" },
          settings: { broker: "oanda", symbol: "GBP/USD" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "Economic calendar & news",
          params: {
            sources: "Forex Factory, Investing.com",
            events: "NFP,CPI,FOMC,BOE",
          },
          settings: { marketDataConnections: { forexfactory: true } },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "sent_1",
        type: "sentimentAnalyst",
        position: { x: 300, y: 250 },
        data: {
          description: "News sentiment analysis",
          params: { sources: "Reuters, Bloomberg" },
          inputs: [],
          outputs: ["sentiment_score"],
        },
      },
      {
        id: "cond_1",
        type: "condition",
        position: { x: 550, y: 50 },
        data: {
          description: "High-impact news filter",
          params: { condition: "Impact == HIGH AND TimeToRelease < 30min" },
          inputs: ["sentiment"],
          outputs: ["filtered"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 750, y: 150 },
        data: {
          description: "AI news impact prediction",
          params: {
            model: "GPT-4",
            strategy: "News Reaction",
            confidence: "0.65",
          },
          inputs: ["market_data", "filtered", "sentiment_score"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 1000, y: 150 },
        data: {
          description: "News trading risk",
          params: {
            maxRisk: "0.5%",
            leverage: "5x",
            stopLossType: "Fixed",
            stopLoss: "30pips",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1250, y: 150 },
        data: {
          description: "Fast news execution",
          params: { mode: "Auto", confirmRequired: "No", slippage: "3pips" },
          settings: { broker: "oanda", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "ai_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "fund_1",
        target: "cond_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "cond_1",
        target: "ai_1",
        sourceHandle: "filtered",
        targetHandle: "filtered",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "sent_1",
        target: "ai_1",
        sourceHandle: "sentiment_score",
        targetHandle: "sentiment_score",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  forexCarryTrade: {
    name: "Forex Carry Trade",
    description: "Interest rate differential strategy for forex pairs",
    category: "Forex",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "High-yield pair data",
          params: {
            symbol: "AUD/JPY",
            timeframe: "1d",
            broker: "interactive_brokers",
          },
          settings: { broker: "interactive_brokers", symbol: "AUD/JPY" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 100 },
        data: {
          description: "Interest rate analysis",
          params: {
            sources: "Central Bank Data",
            metrics: "Interest Rate Differential, Swap Rates",
          },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 250 },
        data: {
          description: "Long-term trend filter",
          params: { indicators: "SMA200,ATR", timeframe: "1d" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 550, y: 175 },
        data: {
          description: "AI carry trade optimization",
          params: {
            model: "GPT-4",
            strategy: "Carry Trade",
            horizon: "long-term",
          },
          inputs: ["market_data", "sentiment", "signals"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 175 },
        data: {
          description: "Carry trade risk",
          params: {
            maxRisk: "2%",
            leverage: "3x",
            stopLossType: "Trailing",
            trailingStop: "5%",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1050, y: 175 },
        data: {
          description: "Execute carry trade",
          params: { mode: "Manual", confirmRequired: "Yes" },
          settings: { broker: "interactive_brokers", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "ai_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "fund_1",
        target: "ai_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "tech_1",
        target: "ai_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  // ============================================================================
  // MULTI-ASSET TEMPLATES
  // ============================================================================

  portfolioRebalancing: {
    name: "Portfolio Rebalancing",
    description: "Automated portfolio rebalancing across multiple assets",
    category: "Multi-Asset",
    nodes: [
      {
        id: "ds_btc",
        type: "dataSource",
        position: { x: 50, y: 50 },
        data: {
          description: "BTC price data",
          params: { symbol: "BTC/USD", timeframe: "1d", broker: "binance" },
          settings: { broker: "binance", symbol: "BTC/USD" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "ds_eth",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "ETH price data",
          params: { symbol: "ETH/USD", timeframe: "1d", broker: "binance" },
          settings: { broker: "binance", symbol: "ETH/USD" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "ds_spy",
        type: "dataSource",
        position: { x: 50, y: 250 },
        data: {
          description: "S&P 500 ETF data",
          params: { symbol: "SPY", timeframe: "1d", broker: "alpaca" },
          settings: { broker: "alpaca", symbol: "SPY" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "ds_gld",
        type: "dataSource",
        position: { x: 50, y: 350 },
        data: {
          description: "Gold ETF data",
          params: { symbol: "GLD", timeframe: "1d", broker: "alpaca" },
          settings: { broker: "alpaca", symbol: "GLD" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "agg_1",
        type: "aggregator",
        position: { x: 350, y: 200 },
        data: {
          description: "Portfolio aggregation",
          params: {
            mode: "Portfolio",
            targetAllocation: "BTC:25%,ETH:25%,SPY:30%,GLD:20%",
          },
          inputs: ["market_data"],
          outputs: ["combined"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 600, y: 200 },
        data: {
          description: "AI rebalancing decisions",
          params: {
            model: "GPT-4",
            strategy: "Risk Parity",
            rebalanceThreshold: "5%",
          },
          inputs: ["combined"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 850, y: 200 },
        data: {
          description: "Portfolio risk management",
          params: { maxRisk: "10%", leverage: "1x", correlationLimit: "0.7" },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "order_1",
        type: "orderManager",
        position: { x: 1100, y: 200 },
        data: {
          description: "Multi-broker order routing",
          params: { splitOrders: true, optimizeExecution: true },
          inputs: ["orders"],
          outputs: ["routed_orders"],
        },
      },
      {
        id: "tg_1",
        type: "telegramReport",
        position: { x: 1100, y: 350 },
        data: {
          description: "Portfolio reports",
          params: {
            frequency: "weekly",
            includeCharts: true,
            includeSummary: true,
          },
          inputs: ["routed_orders"],
          outputs: ["status"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_btc",
        target: "agg_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_eth",
        target: "agg_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "ds_spy",
        target: "agg_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "ds_gld",
        target: "agg_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "agg_1",
        target: "ai_1",
        sourceHandle: "combined",
        targetHandle: "combined",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e7",
        source: "risk_1",
        target: "order_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e8",
        source: "order_1",
        target: "tg_1",
        sourceHandle: "routed_orders",
        targetHandle: "routed_orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  crossAssetArbitrage: {
    name: "Cross-Asset Arbitrage",
    description: "Exploit price discrepancies across different exchanges",
    category: "Multi-Asset",
    nodes: [
      {
        id: "ds_binance",
        type: "dataSource",
        position: { x: 50, y: 100 },
        data: {
          description: "Binance BTC price",
          params: { symbol: "BTC/USDT", timeframe: "1m", broker: "binance" },
          settings: { broker: "binance", symbol: "BTC/USDT" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "ds_coinbase",
        type: "dataSource",
        position: { x: 50, y: 250 },
        data: {
          description: "Coinbase BTC price",
          params: { symbol: "BTC/USD", timeframe: "1m", broker: "coinbase" },
          settings: { broker: "coinbase", symbol: "BTC/USD" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "agg_1",
        type: "aggregator",
        position: { x: 300, y: 175 },
        data: {
          description: "Price comparison",
          params: { mode: "Arbitrage", spreadThreshold: "0.5%" },
          inputs: ["market_data"],
          outputs: ["combined"],
        },
      },
      {
        id: "cond_1",
        type: "condition",
        position: { x: 550, y: 175 },
        data: {
          description: "Arbitrage opportunity filter",
          params: { condition: "Spread > 0.3% AND Volume > $100k" },
          inputs: ["combined"],
          outputs: ["filtered"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 175 },
        data: {
          description: "Arbitrage risk limits",
          params: { maxRisk: "1%", leverage: "1x", maxExposure: "$50000" },
          inputs: ["filtered"],
          outputs: ["orders"],
        },
      },
      {
        id: "order_1",
        type: "orderManager",
        position: { x: 1050, y: 175 },
        data: {
          description: "Simultaneous order execution",
          params: { syncExecution: true, maxLatency: "100ms" },
          inputs: ["orders"],
          outputs: ["routed_orders"],
        },
      },
      {
        id: "mem_1",
        type: "memory",
        position: { x: 1050, y: 325 },
        data: {
          description: "Arbitrage history",
          params: { capacity: "1000", persistenceMode: "database" },
          inputs: ["routed_orders"],
          outputs: ["history"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_binance",
        target: "agg_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_coinbase",
        target: "agg_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "agg_1",
        target: "cond_1",
        sourceHandle: "combined",
        targetHandle: "combined",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "cond_1",
        target: "risk_1",
        sourceHandle: "filtered",
        targetHandle: "filtered",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "order_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "order_1",
        target: "mem_1",
        sourceHandle: "routed_orders",
        targetHandle: "routed_orders",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  aiFullAuto: {
    name: "AI Full Automation",
    description: "Fully automated AI-driven trading across all asset classes",
    category: "Advanced",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 200 },
        data: {
          description: "Smart Symbol Discovery",
          params: { symbol: "AUTO", timeframe: "1h", broker: "binance" },
          settings: { broker: "binance", smartSymbolEnabled: true },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 100 },
        data: {
          description: "Technical analysis",
          params: { indicators: "RSI,MACD,BB,ATR,Volume", timeframe: "1h" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 300 },
        data: {
          description: "News & sentiment",
          params: { sources: "All Available" },
          settings: {
            socialMediaConnections: { twitter: true, reddit: true },
            marketDataConnections: { alphavantage: true },
            cryptoAnalyticsConnections: { glassnode: true },
          },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "mtf_1",
        type: "multiTimeframe",
        position: { x: 550, y: 100 },
        data: {
          description: "Multi-timeframe analysis",
          params: { timeframes: "15m,1h,4h,1d" },
          inputs: ["market_data"],
          outputs: ["mtf_signals"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 750, y: 200 },
        data: {
          description: "GPT-4 Trading Brain",
          params: {
            model: "GPT-4",
            strategy: "Adaptive",
            confidence: "0.7",
            learningEnabled: true,
          },
          inputs: ["signals", "sentiment", "mtf_signals"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 1000, y: 200 },
        data: {
          description: "Dynamic risk management",
          params: {
            maxRisk: "2%",
            leverage: "Auto",
            stopLossType: "Dynamic",
            drawdownLimit: "10%",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1250, y: 200 },
        data: {
          description: "Smart execution",
          params: {
            mode: "Auto",
            confirmRequired: "No",
            slippageProtection: true,
          },
          settings: { broker: "binance", paperTrading: "Yes" },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "mem_1",
        type: "memory",
        position: { x: 1250, y: 350 },
        data: {
          description: "Trade history & learning",
          params: {
            capacity: "10000",
            persistenceMode: "database",
            enableLearning: true,
          },
          inputs: ["trades"],
          outputs: ["history"],
        },
      },
      {
        id: "tg_1",
        type: "telegramReport",
        position: { x: 1000, y: 350 },
        data: {
          description: "Real-time alerts",
          params: {
            frequency: "realtime",
            alertOnTrades: true,
            includeCharts: true,
            includeSummary: true,
          },
          inputs: ["trades"],
          outputs: ["status"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "mtf_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "tech_1",
        target: "ai_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "fund_1",
        target: "ai_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "mtf_1",
        target: "ai_1",
        sourceHandle: "mtf_signals",
        targetHandle: "mtf_signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e7",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e8",
        source: "exec_1",
        target: "mem_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e9",
        source: "exec_1",
        target: "tg_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  // ============================================================================
  // KUCOIN TEMPLATES
  // ============================================================================

  kucoinMomentum: {
    name: "KuCoin Momentum Strategy",
    description: "RSI and MACD based momentum trading on KuCoin exchange",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "KuCoin market data feed",
          params: { symbol: "BTC/USDT", timeframe: "4h", broker: "kucoin" },
          settings: { broker: "kucoin", symbol: "BTC/USDT", timeframe: "4h" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 100 },
        data: {
          description: "Momentum indicators (RSI, MACD, EMA)",
          params: { indicators: "RSI,MACD,EMA20,EMA50", timeframe: "4h" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "cond_1",
        type: "condition",
        position: { x: 300, y: 250 },
        data: {
          description: "Entry conditions check",
          params: { condition: "RSI < 30 OR RSI > 70", action: "trigger" },
          inputs: ["market_data"],
          outputs: ["trigger"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 550, y: 150 },
        data: {
          description: "Risk management for KuCoin trades",
          params: {
            maxRisk: "2%",
            leverage: "1x",
            stopLossType: "ATR",
            stopLoss: "2%",
            takeProfit: "4%",
          },
          inputs: ["signals", "trigger"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 800, y: 150 },
        data: {
          description: "Execute on KuCoin",
          params: { mode: "Auto", confirmRequired: "No", broker: "kucoin" },
          settings: { broker: "kucoin", paperTrading: true },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "mem_1",
        type: "memory",
        position: { x: 800, y: 300 },
        data: {
          description: "Trade history storage",
          params: { storageType: "Database", retention: "90 days" },
          inputs: ["trades"],
          outputs: ["history"],
        },
      },
      {
        id: "sched_1",
        type: "scheduler",
        position: { x: 50, y: 300 },
        data: {
          description: "Automated scheduling with confidence filter",
          params: {
            enabled: "Yes",
            interval: "1h",
            confidenceThreshold: "70",
            autoExecute: "No",
            maxSignalsPerRun: "5",
            paperTrading: "Yes",
          },
          settings: {
            enabled: "Yes",
            interval: "1h",
            confidenceThreshold: "70",
            autoExecute: "No",
            maxSignalsPerRun: "5",
            tradingHoursStart: "00:00",
            tradingHoursEnd: "23:59",
            daysActive: "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
            paperTrading: "Yes",
          },
          inputs: ["signals"],
          outputs: ["filtered_signals", "auto_trades"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "cond_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "tech_1",
        target: "risk_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "cond_1",
        target: "risk_1",
        sourceHandle: "trigger",
        targetHandle: "trigger",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "exec_1",
        target: "mem_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e7",
        source: "tech_1",
        target: "sched_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  kucoinDCA: {
    name: "KuCoin DCA Bot",
    description: "Dollar Cost Averaging strategy for accumulating crypto on KuCoin",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "KuCoin price data",
          params: { symbol: "ETH/USDT", timeframe: "1d", broker: "kucoin" },
          settings: { broker: "kucoin", symbol: "ETH/USDT", timeframe: "1d" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "cond_1",
        type: "condition",
        position: { x: 300, y: 100 },
        data: {
          description: "DCA schedule trigger",
          params: { condition: "schedule", interval: "weekly", day: "Monday" },
          inputs: ["market_data"],
          outputs: ["trigger"],
        },
      },
      {
        id: "cond_2",
        type: "condition",
        position: { x: 300, y: 250 },
        data: {
          description: "Price dip detector (optional extra buy)",
          params: { condition: "price_drop > 10%", lookback: "7d" },
          inputs: ["market_data"],
          outputs: ["dip_trigger"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 550, y: 150 },
        data: {
          description: "DCA position sizing",
          params: {
            fixedAmount: "$100",
            maxPosition: "10 ETH",
            leverage: "1x",
          },
          inputs: ["trigger", "dip_trigger"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 800, y: 150 },
        data: {
          description: "Execute DCA buy on KuCoin",
          params: { mode: "Auto", confirmRequired: "No", broker: "kucoin" },
          settings: { broker: "kucoin", paperTrading: true },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "tg_1",
        type: "telegramReport",
        position: { x: 800, y: 300 },
        data: {
          description: "DCA purchase notifications",
          params: {
            frequency: "on_trade",
            alertOnTrades: true,
            includeSummary: true,
          },
          inputs: ["trades"],
          outputs: ["status"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "cond_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "cond_2",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "cond_1",
        target: "risk_1",
        sourceHandle: "trigger",
        targetHandle: "trigger",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "cond_2",
        target: "risk_1",
        sourceHandle: "dip_trigger",
        targetHandle: "dip_trigger",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "exec_1",
        target: "tg_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  kucoinGridTrading: {
    name: "KuCoin Grid Trading",
    description: "Automated grid trading strategy for range-bound markets on KuCoin",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "KuCoin real-time data",
          params: { symbol: "SOL/USDT", timeframe: "15m", broker: "kucoin" },
          settings: { broker: "kucoin", symbol: "SOL/USDT", timeframe: "15m" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "tech_1",
        type: "technicalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "Range detection (Bollinger Bands, ATR)",
          params: { indicators: "BB,ATR,Support,Resistance", timeframe: "15m" },
          inputs: ["market_data"],
          outputs: ["signals"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 300, y: 200 },
        data: {
          description: "Grid level calculator",
          params: {
            model: "GPT-4",
            strategy: "Grid Trading",
            gridLevels: "10",
            gridSpacing: "1%",
          },
          inputs: ["market_data", "signals"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 550, y: 150 },
        data: {
          description: "Grid order sizing",
          params: {
            maxRisk: "5%",
            leverage: "1x",
            orderSize: "equal",
            totalCapital: "$1000",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 800, y: 150 },
        data: {
          description: "Execute grid orders on KuCoin",
          params: { mode: "Auto", confirmRequired: "No", broker: "kucoin" },
          settings: { broker: "kucoin", paperTrading: true },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "mem_1",
        type: "memory",
        position: { x: 800, y: 300 },
        data: {
          description: "Grid trade history",
          params: { storageType: "Database", retention: "180 days" },
          inputs: ["trades"],
          outputs: ["history"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "tech_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "ds_1",
        target: "ai_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "tech_1",
        target: "ai_1",
        sourceHandle: "signals",
        targetHandle: "signals",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "exec_1",
        target: "mem_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
    ],
  },

  kucoinWhaleAlert: {
    name: "KuCoin Whale Alert Strategy",
    description: "Follow whale movements using on-chain analytics for KuCoin trading",
    category: "Crypto",
    nodes: [
      {
        id: "ds_1",
        type: "dataSource",
        position: { x: 50, y: 150 },
        data: {
          description: "KuCoin market data",
          params: { symbol: "BTC/USDT", timeframe: "1h", broker: "kucoin" },
          settings: { broker: "kucoin", symbol: "BTC/USDT", timeframe: "1h" },
          inputs: [],
          outputs: ["market_data"],
        },
      },
      {
        id: "fund_1",
        type: "fundamentalAnalyst",
        position: { x: 300, y: 50 },
        data: {
          description: "Whale Alert & On-chain data",
          params: { sources: "Whale Alert, CryptoQuant, Glassnode" },
          settings: {
            cryptoAnalyticsConnections: {
              whaleAlert: true,
              cryptoQuant: true,
              glassnode: true,
            },
          },
          inputs: [],
          outputs: ["sentiment"],
        },
      },
      {
        id: "sent_1",
        type: "sentimentAnalyst",
        position: { x: 300, y: 250 },
        data: {
          description: "Crypto Twitter sentiment",
          params: { sources: "Twitter, Reddit" },
          inputs: [],
          outputs: ["sentiment_score"],
        },
      },
      {
        id: "ai_1",
        type: "aiDecision",
        position: { x: 550, y: 150 },
        data: {
          description: "AI whale movement analysis",
          params: {
            model: "GPT-4",
            strategy: "Whale Following",
            minWhaleSize: "$1M",
            confidence: "0.7",
          },
          inputs: ["market_data", "sentiment", "sentiment_score"],
          outputs: ["decisions"],
        },
      },
      {
        id: "risk_1",
        type: "riskManager",
        position: { x: 800, y: 100 },
        data: {
          description: "Conservative whale trade sizing",
          params: {
            maxRisk: "3%",
            leverage: "1x",
            stopLossType: "Trailing",
            trailingStop: "5%",
          },
          inputs: ["decisions"],
          outputs: ["orders"],
        },
      },
      {
        id: "exec_1",
        type: "executionLayer",
        position: { x: 1050, y: 100 },
        data: {
          description: "Execute on KuCoin",
          params: { mode: "Auto", confirmRequired: "No", broker: "kucoin" },
          settings: { broker: "kucoin", paperTrading: true },
          inputs: ["orders"],
          outputs: ["trades"],
        },
      },
      {
        id: "tg_1",
        type: "telegramReport",
        position: { x: 1050, y: 250 },
        data: {
          description: "Whale alert notifications",
          params: {
            frequency: "realtime",
            alertOnTrades: true,
            includeCharts: true,
            includeSummary: true,
          },
          inputs: ["trades"],
          outputs: ["status"],
        },
      },
    ],
    edges: [
      {
        id: "e1",
        source: "ds_1",
        target: "ai_1",
        sourceHandle: "market_data",
        targetHandle: "market_data",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e2",
        source: "fund_1",
        target: "ai_1",
        sourceHandle: "sentiment",
        targetHandle: "sentiment",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e3",
        source: "sent_1",
        target: "ai_1",
        sourceHandle: "sentiment_score",
        targetHandle: "sentiment_score",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e4",
        source: "ai_1",
        target: "risk_1",
        sourceHandle: "decisions",
        targetHandle: "decisions",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e5",
        source: "risk_1",
        target: "exec_1",
        sourceHandle: "orders",
        targetHandle: "orders",
        animated: true,
        type: "smoothstep",
      },
      {
        id: "e6",
        source: "exec_1",
        target: "tg_1",
        sourceHandle: "trades",
        targetHandle: "trades",
        animated: true,
        type: "smoothstep",
      },
    ],
  },
};

function StrategyBuilder() {
  // Create a completely new implementation of undo/redo functionality
  // This creates a base setup with built-in ReactFlow state management
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Simple history management for undo/redo without complex flags
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const isUndoRedoing = useRef(false);

  // Create flow wrapper ref for keyboard events
  const reactFlowWrapper = useRef(null);

  // Function to clear the canvas
  const handleClearCanvas = () => {
    setClearDialogOpen(true);
  };

  // Function to confirm clearing the canvas
  const confirmClearCanvas = () => {
    setNodes([]);
    setEdges([]);
    setClearDialogOpen(false);
  };

  // Function to cancel clearing the canvas
  const cancelClearCanvas = () => {
    setClearDialogOpen(false);
  };

  // Initialize undo stack with initial state when component mounts
  useEffect(() => {
    // Save initial state to the undo stack
    const initialState = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };
    // Initialize with the initial state
    setUndoStack([initialState]);
    // Clear redo stack
    setRedoStack([]);
    // We intentionally only run this once on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save the current state to the undo stack
  const saveToHistory = useCallback(() => {
    if (isUndoRedoing.current) return;

    const currentState = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };

    setUndoStack((prev) => [...prev, currentState]);
    // Clear the redo stack when a new action is performed
    setRedoStack([]);
    console.log("Saved to undo stack");
  }, [nodes, edges]);

  // Handle undo operation
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;

    // Mark that we're performing an undo/redo operation
    isUndoRedoing.current = true;

    // Get the last state from undo stack
    const newUndoStack = [...undoStack];
    const prevState = newUndoStack.pop();

    // Save current state to redo stack before reverting
    const currentState = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };

    // Update stacks
    setRedoStack((prev) => [...prev, currentState]);
    setUndoStack(newUndoStack);

    // Restore previous state
    console.log("Undoing to previous state");
    setNodes(prevState.nodes);
    setEdges(prevState.edges);

    // Reset the flag after a short delay
    setTimeout(() => {
      isUndoRedoing.current = false;
    }, 50);
  }, [undoStack, nodes, edges, setNodes, setEdges]);

  // Handle redo operation
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;

    // Mark that we're performing an undo/redo operation
    isUndoRedoing.current = true;

    // Get the last state from redo stack
    const newRedoStack = [...redoStack];
    const nextState = newRedoStack.pop();

    // Save current state to undo stack before applying redo
    const currentState = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    };

    // Update stacks
    setUndoStack((prev) => [...prev, currentState]);
    setRedoStack(newRedoStack);

    // Apply the redo state
    console.log("Redoing to next state");
    setNodes(nextState.nodes);
    setEdges(nextState.edges);

    // Reset the flag after a short delay
    setTimeout(() => {
      isUndoRedoing.current = false;
    }, 50);
  }, [redoStack, nodes, edges, setNodes, setEdges]);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [strategyName, setStrategyName] = useState("New Trading Strategy");
  const [strategyDescription, setStrategyDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateCategoryTab, setTemplateCategoryTab] = useState("all");
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isStrategyManagerOpen, setIsStrategyManagerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [strategyTags, setStrategyTags] = useState([]);
  const [strategyNotes, setStrategyNotes] = useState("");

  // Reporting settings
  const [enableReporting, setEnableReporting] = useState(false);

  // Strategy-level active symbol (for Smart Symbol Discovery propagation)
  const [activeStrategySymbol, setActiveStrategySymbol] = useState(null);

  // Strategy-level active broker (for broker synchronization)
  const [activeStrategyBroker, setActiveStrategyBroker] = useState(null);

  // Strategy-level paper trading mode (affects all broker communications)
  const [isPaperTrading, setIsPaperTrading] = useState(true); // Default to paper trading for safety

  // Function to propagate symbol to downstream nodes
  const propagateSymbolToDownstream = useCallback(
    (sourceNodeId, symbol, currentNodes, currentEdges) => {
      // Find all downstream nodes connected to this source
      const getDownstreamNodes = (nodeId, visited = new Set()) => {
        if (visited.has(nodeId)) return [];
        visited.add(nodeId);

        const downstreamEdges = currentEdges.filter((e) => e.source === nodeId);
        const downstreamNodeIds = downstreamEdges.map((e) => e.target);

        let allDownstream = [...downstreamNodeIds];
        for (const id of downstreamNodeIds) {
          allDownstream = [
            ...allDownstream,
            ...getDownstreamNodes(id, visited),
          ];
        }
        return allDownstream;
      };

      const downstreamNodeIds = getDownstreamNodes(sourceNodeId);

      // Update all downstream nodes with inherited symbol
      const updatedNodes = currentNodes.map((node) => {
        if (downstreamNodeIds.includes(node.id)) {
          return {
            ...node,
            data: {
              ...node.data,
              inheritedSymbol: symbol,
              inheritedFrom: sourceNodeId,
            },
          };
        }
        return node;
      });

      return updatedNodes;
    },
    [],
  );

  // Function to set strategy-wide symbol (from Smart Symbol Discovery)
  const setStrategySymbol = useCallback(
    (symbol, sourceNodeId) => {
      setActiveStrategySymbol(symbol);

      // Update all nodes to use this symbol
      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          // Skip the source node itself
          if (node.id === sourceNodeId) return node;

          // Update all other nodes with the smart symbol
          return {
            ...node,
            data: {
              ...node.data,
              inheritedSymbol: symbol,
              inheritedFrom: sourceNodeId,
              inheritedType: "smart",
            },
          };
        });
      });
    },
    [setNodes],
  );

  // Function to propagate broker to downstream nodes
  const propagateBrokerToDownstream = useCallback(
    (sourceNodeId, broker, currentNodes, currentEdges) => {
      // Find all downstream nodes connected to this source
      const getDownstreamNodes = (nodeId, visited = new Set()) => {
        if (visited.has(nodeId)) return [];
        visited.add(nodeId);

        const downstreamEdges = currentEdges.filter((e) => e.source === nodeId);
        const downstreamNodeIds = downstreamEdges.map((e) => e.target);

        let allDownstream = [...downstreamNodeIds];
        for (const id of downstreamNodeIds) {
          allDownstream = [
            ...allDownstream,
            ...getDownstreamNodes(id, visited),
          ];
        }
        return allDownstream;
      };

      const downstreamNodeIds = getDownstreamNodes(sourceNodeId);

      // Update all downstream nodes with inherited broker
      // Only update nodes that use broker connections
      const brokerNodeTypes = [
        "dataSource",
        "technicalAnalyst",
        "executionLayer",
        "fundamentalAnalyst",
        "scheduler",
      ];

      const updatedNodes = currentNodes.map((node) => {
        if (
          downstreamNodeIds.includes(node.id) &&
          brokerNodeTypes.includes(node.type)
        ) {
          return {
            ...node,
            data: {
              ...node.data,
              inheritedBroker: broker,
              inheritedBrokerFrom: sourceNodeId,
            },
          };
        }
        return node;
      });

      return updatedNodes;
    },
    [],
  );

  // Function to set strategy-wide broker
  const setStrategyBroker = useCallback(
    (broker, sourceNodeId) => {
      setActiveStrategyBroker(broker);

      // Update all broker-using nodes
      const brokerNodeTypes = [
        "dataSource",
        "technicalAnalyst",
        "executionLayer",
        "fundamentalAnalyst",
        "scheduler",
      ];

      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          // Skip the source node itself
          if (node.id === sourceNodeId) return node;

          // Only update nodes that use broker connections
          if (!brokerNodeTypes.includes(node.type)) return node;

          return {
            ...node,
            data: {
              ...node.data,
              inheritedBroker: broker,
              inheritedBrokerFrom: sourceNodeId,
              inheritedBrokerType: "strategy",
            },
          };
        });
      });
    },
    [setNodes],
  );

  // Template dialog handlers
  const handleOpenTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(true);
  }, []);

  const handleCloseTemplateDialog = useCallback(() => {
    setIsTemplateDialogOpen(false);
  }, []);
  const [reportFrequency, setReportFrequency] = useState("daily");
  const [enableTelegram, setEnableTelegram] = useState(false);
  const [enableEmail, setEnableEmail] = useState(false);
  const [telegramToken, setTelegramToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [emailAddress, setEmailAddress] = useState("");

  // Cloud strategies from Strategy Engine API
  const [strategies, setStrategies] = useState([]);
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const [currentStrategyId, setCurrentStrategyId] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [runStatus, setRunStatus] = useState(null);
  
  // Concurrent execution status for all strategies
  const [executionStatus, setExecutionStatus] = useState({ strategies: [], conflicts: [] });
  
  // Strategy Templates & Cloning
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [saveTemplateStrategyId, setSaveTemplateStrategyId] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const [cloneName, setCloneName] = useState("");
  const [loadTemplateDialogOpen, setLoadTemplateDialogOpen] = useState(false);
  const [loadTemplateId, setLoadTemplateId] = useState(null);
  const [loadTemplateName, setLoadTemplateName] = useState("");
  
  // Pending Signals (Confirmation Mode)
  const [pendingSignals, setPendingSignals] = useState([]);
  const [soundMuted, setSoundMuted] = useState(false);
  const prevPendingCountRef = useRef(0);
  const prevPositionCountRef = useRef(0);
  
  // Live run status panel
  const [showRunPanel, setShowRunPanel] = useState(false);
  const [runDetails, setRunDetails] = useState({
    completedNodes: 0,
    totalNodes: 0,
    currentNode: null,
    nodeResults: [],
    positions: [],
    trades: [],
    signals: [],
    errors: [],
    startTime: null,
    lastUpdate: null,
    globalPositionSize: { usdValue: 100, budgetPercent: 5 },
    signalSizes: [],
    allOpenPositions: [], // All open positions across all runs for "Hide Open" filtering
    capitalStatus: null, // Money management capital status
    moneyManagementSettings: null, // Money management settings
  });

  // Load strategies and templates from cloud on mount
  useEffect(() => {
    loadStrategiesFromCloud();
    loadTemplatesFromCloud();
    checkForActiveRuns();
    loadExecutionStatus();
  }, []);
  
  // Periodically refresh execution status for concurrent strategies
  useEffect(() => {
    const interval = setInterval(loadExecutionStatus, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);
  
  // Load execution status for all active strategies
  const loadExecutionStatus = async () => {
    try {
      const status = await StrategyEngineService.getExecutionStatus();
      setExecutionStatus(status);
    } catch (error) {
      console.error("Error loading execution status:", error);
    }
  };

  // Toggle strategy schedule on/off (quick activate/deactivate)
  const toggleStrategySchedule = async (strategyId, e) => {
    if (e) e.stopPropagation();
    
    // Optimistic UI update — flip the switch immediately
    setExecutionStatus(prev => ({
      ...prev,
      strategies: (prev.strategies || []).map(s =>
        s.strategyId === strategyId ? { ...s, enabled: !s.enabled } : s
      ),
    }));
    
    try {
      const result = await StrategyEngineService.toggleSchedule(strategyId);
      console.log(`Strategy ${strategyId} toggled:`, result);
      // Confirm with fresh server state
      await loadExecutionStatus();
    } catch (error) {
      console.error("Error toggling strategy:", error);
      // Revert optimistic update on failure
      await loadExecutionStatus();
      alert(`Failed to toggle strategy: ${error.message}`);
    }
  };

  // Periodically update position P&L with real-time prices
  useEffect(() => {
    if (!currentRunId || runDetails.positions.length === 0) return;
    
    const updatePositionPnL = async () => {
      const openPositions = runDetails.positions.filter(p => p.status === 'open');
      if (openPositions.length === 0) return;
      
      try {
        // Fetch updated positions from backend (which fetches current prices)
        const updatedPositions = await StrategyEngineService.getRunPositions(currentRunId);
        if (updatedPositions && updatedPositions.length > 0) {
          setRunDetails(prev => ({
            ...prev,
            positions: updatedPositions,
            lastUpdate: new Date().toISOString(),
          }));
        }
      } catch (error) {
        console.error("Error updating position P&L:", error);
      }
    };
    
    // Update immediately and then every 5 seconds
    updatePositionPnL();
    const interval = setInterval(updatePositionPnL, 5000);
    
    return () => clearInterval(interval);
  }, [currentRunId, runDetails.positions.length]);

  // Fetch capital status for money management
  useEffect(() => {
    if (!currentRunId) return;
    
    const fetchCapitalStatus = async () => {
      try {
        const capitalStatus = await StrategyEngineService.getCapitalStatus(currentRunId);
        if (capitalStatus) {
          setRunDetails(prev => ({
            ...prev,
            capitalStatus,
            moneyManagementSettings: capitalStatus.settings,
          }));
        }
      } catch (error) {
        console.error("Error fetching capital status:", error);
      }
    };
    
    // Fetch immediately and then every 30 seconds
    fetchCapitalStatus();
    const interval = setInterval(fetchCapitalStatus, 30000);
    
    return () => clearInterval(interval);
  }, [currentRunId]);

  // Fetch all open positions across all runs when "Hide Open" is enabled
  useEffect(() => {
    if (!runDetails.hideOpenSymbols) return;
    
    const fetchAllOpenPositions = async () => {
      try {
        const allPositions = await StrategyEngineService.getAllPositions();
        const openPositions = (allPositions || []).filter(p => p.status === 'open' || p.status === 'pending');
        setRunDetails(prev => ({
          ...prev,
          allOpenPositions: openPositions,
        }));
        console.log(`[HIDE_OPEN] Fetched ${openPositions.length} open positions across all runs`);
      } catch (error) {
        console.error("[HIDE_OPEN] Error fetching all positions:", error);
      }
    };
    
    fetchAllOpenPositions();
    // Refresh every 10 seconds while hide is enabled
    const interval = setInterval(fetchAllOpenPositions, 10000);
    return () => clearInterval(interval);
  }, [runDetails.hideOpenSymbols]);

  // Check for active runs on page load
  const checkForActiveRuns = async () => {
    try {
      // First check for running runs
      const runs = await StrategyEngineService.listRuns({ status: "running" });
      
      // Also check for recently completed runs to show positions
      const completedRuns = await StrategyEngineService.listRuns({ status: "completed", limit: 5 });
      
      // Find the most recent run with positions
      let runToShow = null;
      let positions = [];
      
      // Check running runs first
      if (runs && runs.length > 0) {
        runToShow = runs[0];
        try {
          positions = await StrategyEngineService.getRunPositions(runToShow.id);
        } catch (e) { /* no positions */ }
      }
      
      // If no running run with positions, check completed runs
      if ((!runToShow || positions.length === 0) && completedRuns && completedRuns.length > 0) {
        for (const run of completedRuns) {
          try {
            const runPositions = await StrategyEngineService.getRunPositions(run.id);
            if (runPositions && runPositions.length > 0) {
              runToShow = run;
              positions = runPositions;
              break;
            }
          } catch (e) { /* continue */ }
        }
      }
      
      if (runToShow) {
        const activeRun = runToShow;
        setCurrentRunId(activeRun.id);
        setCurrentStrategyId(activeRun.strategy_id || activeRun.strategyId);
        setIsRunning(activeRun.status === 'running');
        setShowRunPanel(true);
        setRunStatus(activeRun.status === 'running' ? "running" : "completed");
        setRunDetails(prev => ({
          ...prev,
          startTime: activeRun.started_at || activeRun.startedAt,
          positions: positions,
          lastUpdate: new Date().toISOString(),
        }));
        
        // Reconnect to WebSocket for this run
        const subscription = StrategyEngineService.connectToRunWebSocket(
          activeRun.id,
          {
            onProgress: (data) => {
              console.log("Run progress:", data);
              setRunStatus(
                `Running: ${data.completedNodes}/${data.totalNodes} nodes`,
              );
              setRunDetails(prev => ({
                ...prev,
                completedNodes: data.completedNodes || prev.completedNodes,
                totalNodes: data.totalNodes || prev.totalNodes,
                currentNode: data.currentNode || prev.currentNode,
                nodeResults: data.nodeResults ? [...prev.nodeResults, ...data.nodeResults] : prev.nodeResults,
                signals: data.signals ? [...prev.signals, ...data.signals] : prev.signals,
                lastUpdate: new Date().toISOString(),
              }));
            },
            onComplete: (data) => {
              setIsRunning(false);
              setRunStatus("completed");
              setRunDetails(prev => ({
                ...prev,
                completedNodes: prev.totalNodes,
                trades: data.trades || prev.trades,
                positions: data.positions || prev.positions,
                lastUpdate: new Date().toISOString(),
              }));
            },
            onError: (data) => {
              setIsRunning(false);
              setRunStatus("failed");
              setRunDetails(prev => ({
                ...prev,
                errors: [...prev.errors, data.error || "Unknown error"],
                lastUpdate: new Date().toISOString(),
              }));
            },
            onDisconnect: () => {
              console.log("WebSocket disconnected");
            },
          },
        );
        window.currentRunSubscription = subscription;
      }
    } catch (error) {
      console.error("Error checking for active runs:", error);
    }
  };

  // Load strategies from Strategy Engine API
  const loadStrategiesFromCloud = async () => {
    setIsLoadingStrategies(true);
    try {
      const cloudStrategies = await StrategyEngineService.listStrategies();
      setStrategies(
        cloudStrategies.map((s) => ({
          id: s.id,
          name: s.name,
          description:
            s.description || `Strategy with ${s.nodes?.length || 0} nodes`,
          favorite: false,
          lastUpdated:
            s.updatedAt?.split("T")[0] ||
            new Date().toISOString().split("T")[0],
          tags: s.tags || [],
          notes: "",
          nodes: s.nodes || [],
          edges: s.edges || [],
          status: s.status,
        })),
      );
    } catch (error) {
      console.error("Error loading strategies from cloud:", error);
    } finally {
      setIsLoadingStrategies(false);
    }
  };

  // Load templates from cloud
  const loadTemplatesFromCloud = async () => {
    setIsLoadingTemplates(true);
    try {
      const templates = await StrategyEngineService.listTemplates();
      setSavedTemplates(templates || []);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Save strategy as template
  const handleSaveAsTemplate = async () => {
    if (!saveTemplateStrategyId || !templateName.trim()) return;
    try {
      await StrategyEngineService.saveAsTemplate(saveTemplateStrategyId, templateName.trim(), templateDescription.trim());
      setSaveTemplateDialogOpen(false);
      setTemplateName("");
      setTemplateDescription("");
      setSaveTemplateStrategyId(null);
      await loadTemplatesFromCloud();
      alert(`Template "${templateName.trim()}" saved successfully!`);
    } catch (error) {
      alert(`Error saving template: ${error.message}`);
    }
  };

  // Clone strategy
  const handleCloneStrategy = async () => {
    if (!cloneSourceId) return;
    try {
      const cloned = await StrategyEngineService.cloneStrategy(cloneSourceId, cloneName.trim() || null);
      setCloneDialogOpen(false);
      setCloneName("");
      setCloneSourceId(null);
      await loadStrategiesFromCloud();
      if (cloned && cloned.id) {
        loadStrategyFromCloud(cloned.id);
      }
      alert(`Strategy cloned successfully!`);
    } catch (error) {
      alert(`Error cloning strategy: ${error.message}`);
    }
  };

  // Load strategy from template
  const handleLoadFromTemplate = async () => {
    if (!loadTemplateId) return;
    try {
      const created = await StrategyEngineService.createFromTemplate(loadTemplateId, loadTemplateName.trim() || null);
      setLoadTemplateDialogOpen(false);
      setLoadTemplateId(null);
      setLoadTemplateName("");
      await loadStrategiesFromCloud();
      if (created && created.id) {
        loadStrategyFromCloud(created.id);
      }
      alert(`Strategy created from template!`);
    } catch (error) {
      alert(`Error loading from template: ${error.message}`);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"? This cannot be undone.`)) return;
    try {
      await StrategyEngineService.deleteTemplate(templateId);
      await loadTemplatesFromCloud();
    } catch (error) {
      alert(`Error deleting template: ${error.message}`);
    }
  };

  // Sound notification helper
  const playNotificationSound = useCallback(() => {
    if (soundMuted) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch (e) {
      // Audio not available
    }
  }, [soundMuted]);

  // Poll pending signals every 10 seconds
  const loadPendingSignals = useCallback(async () => {
    try {
      const signals = await StrategyEngineService.listPendingSignals();
      setPendingSignals(signals || []);
      // Play sound if new signals arrived
      if (signals.length > prevPendingCountRef.current && prevPendingCountRef.current >= 0) {
        playNotificationSound();
      }
      prevPendingCountRef.current = signals.length;
    } catch (error) {
      console.error("Error loading pending signals:", error);
    }
  }, [playNotificationSound]);

  useEffect(() => {
    loadPendingSignals();
    const interval = setInterval(loadPendingSignals, 10000);
    return () => clearInterval(interval);
  }, [loadPendingSignals]);

  // Also play sound when new positions appear
  useEffect(() => {
    const openCount = runDetails.positions.filter(p => p.status === 'open').length;
    if (openCount > prevPositionCountRef.current && prevPositionCountRef.current >= 0) {
      playNotificationSound();
    }
    prevPositionCountRef.current = openCount;
  }, [runDetails.positions, playNotificationSound]);

  // Confirm pending signal
  const handleConfirmSignal = async (signalId) => {
    try {
      const result = await StrategyEngineService.confirmSignal(signalId);
      await loadPendingSignals();
      alert(`Position opened at ${result.executedPrice} (${result.priceChange} from signal)`);
    } catch (error) {
      alert(`Error confirming signal: ${error.message}`);
    }
  };

  // Reject pending signal
  const handleRejectSignal = async (signalId) => {
    try {
      await StrategyEngineService.rejectSignal(signalId);
      await loadPendingSignals();
    } catch (error) {
      alert(`Error rejecting signal: ${error.message}`);
    }
  };

  // Save strategy to cloud
  const saveStrategyToCloud = async () => {
    setIsSaving(true);
    try {
      const strategyData = {
        name: strategyName,
        description:
          strategyDescription ||
          `Strategy with ${nodes.length} nodes and ${edges.length} connections`,
        nodes: nodes,
        edges: edges,
        tags: strategyTags,
        settings: {
          schedule: "manual",
          maxRiskPercent: 2,
          onError: "stop",
          notifications: "all",
          paperTrading: isPaperTrading,
        },
      };

      let savedStrategy;
      if (currentStrategyId) {
        // Update existing strategy
        savedStrategy = await StrategyEngineService.updateStrategy(
          currentStrategyId,
          {
            ...strategyData,
            changeSummary: `Updated at ${new Date().toISOString()}`,
          },
        );
      } else {
        // Create new strategy
        savedStrategy =
          await StrategyEngineService.createStrategy(strategyData);
        setCurrentStrategyId(savedStrategy.id);
      }

      // Reload strategies list
      await loadStrategiesFromCloud();
      setSaveDialogOpen(false);
      alert(`Strategy "${strategyName}" saved to cloud successfully!`);
    } catch (error) {
      console.error("Error saving strategy to cloud:", error);
      alert(`Error saving strategy: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Load a strategy from cloud
  const loadStrategyFromCloud = async (strategyId) => {
    try {
      const strategy = await StrategyEngineService.getStrategy(strategyId);
      if (strategy) {
        setNodes(strategy.nodes || []);
        setEdges(strategy.edges || []);
        setStrategyName(strategy.name);
        setStrategyDescription(strategy.description || "");
        setStrategyTags(strategy.tags || []);
        setCurrentStrategyId(strategy.id);
        // Restore paper trading setting (default to true for safety)
        setIsPaperTrading(strategy.settings?.paperTrading !== false);
        alert(`Strategy "${strategy.name}" loaded successfully!`);
      } else {
        alert('Failed to load strategy. Please check your connection and try again.');
      }
    } catch (error) {
      console.error("Error loading strategy:", error);
      alert(`Error loading strategy: ${error.message}`);
    }
  };

  // Helper function to save strategy and return the ID
  const saveAndGetStrategyId = async () => {
    const strategyData = {
      name: strategyName,
      description: strategyDescription || `Strategy with ${nodes.length} nodes`,
      nodes: nodes,
      edges: edges,
      tags: strategyTags,
      settings: {
        schedule: "manual",
        maxRiskPercent: 2,
        onError: "stop",
        notifications: "all",
        paperTrading: isPaperTrading,
      },
    };
    const savedStrategy = await StrategyEngineService.createStrategy(strategyData);
    if (savedStrategy && savedStrategy.id) {
      setCurrentStrategyId(savedStrategy.id);
      return savedStrategy.id;
    }
    return null;
  };

  // Run a strategy (optionally pass strategyId directly to avoid stale state)
  const runStrategy = async (overrideStrategyId = null) => {
    let strategyIdToRun = overrideStrategyId || currentStrategyId;
    
    // If no strategy ID, save first
    if (!strategyIdToRun) {
      const shouldSave = window.confirm("Strategy must be saved before running. Save now?");
      if (shouldSave) {
        try {
          strategyIdToRun = await saveAndGetStrategyId();
          if (!strategyIdToRun) {
            alert("Failed to save strategy. Please try saving manually.");
            return;
          }
          alert(`Strategy "${strategyName}" saved! Now starting run...`);
        } catch (error) {
          console.error("Error saving strategy:", error);
          alert(`Error saving strategy: ${error.message}`);
          return;
        }
      } else {
        return;
      }
    }

    setIsRunning(true);
    try {
      const result = await StrategyEngineService.startRun(strategyIdToRun, {
        triggerType: "manual",
        paperTrading: isPaperTrading,
      });
      
      if (!result || !result.runId) {
        throw new Error("Failed to start run - no run ID returned");
      }
      
      setCurrentRunId(result.runId);
      setRunStatus("running");
      setShowRunPanel(true);
      setRunDetails({
        completedNodes: 0,
        totalNodes: nodes.length,
        currentNode: null,
        nodeResults: [],
        positions: [],
        trades: [],
        signals: [],
        errors: [],
        startTime: new Date().toISOString(),
        lastUpdate: new Date().toISOString(),
      });

      // Subscribe to run updates via WebSocket
      const subscription = StrategyEngineService.connectToRunWebSocket(
        result.runId,
        {
          onProgress: (data) => {
            console.log("Run progress:", data);
            setRunStatus(
              `Running: ${data.completedNodes}/${data.totalNodes} nodes`,
            );
            setRunDetails(prev => ({
              ...prev,
              completedNodes: data.completedNodes || prev.completedNodes,
              totalNodes: data.totalNodes || prev.totalNodes,
              currentNode: data.currentNode || prev.currentNode,
              nodeResults: data.nodeResults ? [...prev.nodeResults, ...data.nodeResults] : prev.nodeResults,
              signals: data.signals ? [...prev.signals, ...data.signals] : prev.signals,
              lastUpdate: new Date().toISOString(),
            }));
          },
          onComplete: (data) => {
            setIsRunning(false);
            setRunStatus("completed");
            setRunDetails(prev => ({
              ...prev,
              completedNodes: prev.totalNodes,
              trades: data.trades || prev.trades,
              positions: data.positions || prev.positions,
              lastUpdate: new Date().toISOString(),
            }));
          },
          onError: (data) => {
            setIsRunning(false);
            setRunStatus("failed");
            setRunDetails(prev => ({
              ...prev,
              errors: [...prev.errors, data.error || "Unknown error"],
              lastUpdate: new Date().toISOString(),
            }));
          },
          onDisconnect: () => {
            console.log("WebSocket disconnected");
          },
        },
      );

      // Store subscription for cleanup
      window.currentRunSubscription = subscription;
    } catch (error) {
      console.error("Error starting strategy run:", error);
      const errorMsg = error.message || "";
      // Check if this is a FOREIGN KEY/constraint error (strategy doesn't exist in DB)
      if (errorMsg.includes("FOREIGN KEY") || errorMsg.includes("SQLITE_CONSTRAINT") || errorMsg.includes("not found")) {
        console.log("Strategy not found in DB, attempting to save first...");
        setCurrentStrategyId(null); // Reset the invalid ID
        
        // Offer to save and retry
        const shouldSave = window.confirm("Strategy not found in database. Would you like to save it now and retry?");
        if (shouldSave) {
          try {
            const newId = await saveAndGetStrategyId();
            if (newId) {
              alert(`Strategy saved! Retrying run...`);
              // Retry the run with the new ID
              const retryResult = await StrategyEngineService.startRun(newId, {
                triggerType: "manual",
                paperTrading: isPaperTrading,
              });
              if (retryResult && retryResult.runId) {
                setCurrentRunId(retryResult.runId);
                setRunStatus("running");
                alert(`Strategy run started! Run ID: ${retryResult.runId}`);
                setIsRunning(false);
                return;
              }
            }
          } catch (saveError) {
            console.error("Error saving/retrying:", saveError);
            alert(`Error: ${saveError.message}`);
          }
        }
      } else {
        alert(`Error starting run: ${errorMsg}`);
      }
      setIsRunning(false);
    }
  };

  // Stop a running strategy
  const stopStrategy = async () => {
    if (!currentRunId) return;

    try {
      await StrategyEngineService.stopRun(currentRunId);
      setIsRunning(false);
      setRunStatus("stopped");
      if (window.currentRunSubscription) {
        window.currentRunSubscription.close();
      }
      alert("Strategy run stopped.");
    } catch (error) {
      console.error("Error stopping run:", error);
      alert(`Error stopping run: ${error.message}`);
    }
  };

  // Delete a strategy from cloud
  const deleteStrategyFromCloud = async (strategyId) => {
    if (!window.confirm("Are you sure you want to delete this strategy?")) {
      return;
    }

    try {
      await StrategyEngineService.deleteStrategy(strategyId);
      await loadStrategiesFromCloud();
      if (currentStrategyId === strategyId) {
        setCurrentStrategyId(null);
        setNodes([]);
        setEdges([]);
        setStrategyName("New Strategy");
      }
      alert("Strategy deleted successfully!");
    } catch (error) {
      console.error("Error deleting strategy:", error);
      alert(`Error deleting strategy: ${error.message}`);
    }
  };

  // Define custom connection options
  const connectionLineStyle = {
    strokeWidth: 2,
    stroke: "#ccc",
  };

  // Memoized default edge options to prevent React Flow warnings
  const defaultEdgeOptions = useMemo(
    () => ({
      animated: true,
      type: "smoothstep",
    }),
    [],
  );

  // Track changes to nodes and edges for history
  // Use effect to update onConnect when nodes change
  useEffect(() => {
    // This is just to update dependencies for onConnect
    // No action needed here
  }, [nodes, edges]);

  // Custom handlers that wrap ReactFlow's built-in handlers
  const customNodesChange = useCallback(
    (changes) => {
      // Skip history tracking for node position changes while dragging
      const isDragging = changes.some(
        (change) => change.type === "position" && change.dragging,
      );

      // Only save history when we're not in the middle of a drag operation
      // and also not in the middle of an undo/redo operation
      if (!isDragging && !isUndoRedoing.current) {
        // Store state only on drag end or other significant changes (add/remove)
        const isDragEnd = changes.some(
          (change) => change.type === "position" && change.dragging === false,
        );
        const isSignificantChange = changes.some((change) =>
          ["add", "remove", "select", "reset"].includes(change.type),
        );

        if (isDragEnd || isSignificantChange) {
          // We'll save the state after the change is applied
          setTimeout(() => saveToHistory(), 0);
        }
      }

      // Call the built-in handler from ReactFlow
      onNodesChange(changes);
    },
    [onNodesChange, saveToHistory],
  );

  // Handle edges change with history tracking
  const customEdgesChange = useCallback(
    (changes) => {
      // Track history for all edge changes when not undoing/redoing
      if (!isUndoRedoing.current) {
        // Store state on all edge operations (they're always significant)
        // We'll save the state after the change is applied
        setTimeout(() => saveToHistory(), 0);
      }

      // Call the built-in handler from ReactFlow
      onEdgesChange(changes);
    },
    [onEdgesChange, saveToHistory],
  );

  // Set up keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyboardShortcuts = (event) => {
      // Undo: Ctrl+Z
      if (event.ctrlKey && event.key === "z") {
        event.preventDefault();
        handleUndo();
      }
      // Redo: Ctrl+Y
      if (event.ctrlKey && event.key === "y") {
        event.preventDefault();
        handleRedo();
      }
    };

    document.addEventListener("keydown", handleKeyboardShortcuts);
    return () => {
      document.removeEventListener("keydown", handleKeyboardShortcuts);
    };
  }, [handleUndo, handleRedo]);

  // Handle connections between nodes
  const onConnect = useCallback(
    (params) => {
      // Ensure handle IDs are strings to prevent ReactFlow errors
      const processedParams = {
        ...params,
        sourceHandle: params.sourceHandle ? String(params.sourceHandle) : null,
        targetHandle: params.targetHandle ? String(params.targetHandle) : null,
      };

      const sourceNode = nodes.find(
        (node) => node.id === processedParams.source,
      );
      const targetNode = nodes.find(
        (node) => node.id === processedParams.target,
      );
      const sourceType = sourceNode?.type;
      const targetType = targetNode?.type;

      // Find the node templates for validation
      const sourceTemplate = nodeTemplates.find((t) => t.type === sourceType);
      const targetTemplate = nodeTemplates.find((t) => t.type === targetType);

      // Determine connection capabilities
      const canConnect = sourceTemplate?.outputs && targetTemplate?.inputs;

      if (canConnect) {
        // Get a common data type between source outputs and target inputs
        const commonDataTypes = sourceTemplate.outputs.filter((output) =>
          targetTemplate.inputs.includes(output),
        );

        if (commonDataTypes.length > 0) {
          // Create a new edge with appropriate styling
          const newEdge = {
            ...processedParams,
            id: `e${processedParams.source}-${processedParams.target}`,
            type: "smoothstep",
            animated: true,
            label: commonDataTypes[0],
            labelBgPadding: [8, 4],
            labelBgBorderRadius: 4,
            labelBgStyle: { fill: "#FFCC00", color: "#fff", fillOpacity: 0.7 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: sourceTemplate.color,
            },
          };

          setEdges((eds) => addEdge(newEdge, eds));
          return;
        }
      }

      // Default fallback connection
      const newEdge = {
        ...processedParams,
        id: `e${processedParams.source}-${processedParams.target}`,
        type: "smoothstep",
        animated: false,
        markerEnd: {
          type: MarkerType.ArrowClosed,
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [nodes, setEdges],
  );

  // Handle node click to open settings
  const onNodeClick = useCallback((event, node) => {
    // Make sure node has settings property initialized
    if (!node.data.settings) {
      node.data.settings = {};
    }
    setSelectedNode(node);
    setIsSettingsOpen(true);
  }, []);

  // Add a new node to the graph
  const onAddNode = (type) => {
    const template = nodeTemplates.find((t) => t.type === type);
    const position = {
      x: 100 + Math.random() * 200,
      y: 100 + Math.random() * 200,
    };

    const newNode = {
      id: `node_${Date.now()}`,
      type,
      position,
      data: {
        label: template.label,
        description: template.description,
        settings: {},
        defaultParams: template.defaultParams || {},
        inputs: template.inputs || [],
        outputs: template.outputs || [],
      },
    };

    setNodes((nds) => [...nds, newNode]);
  };

  // Load the selected template
  const handleLoadTemplate = () => {
    if (!selectedTemplate) return;

    const template = strategyTemplates[selectedTemplate];
    setNodes(template.nodes);
    setEdges(template.edges);
    setStrategyName(`New ${template.name}`);
    setStrategyDescription(template.description);
    setCurrentStrategyId(null); // Reset ID - template is a new unsaved strategy
    setIsTemplateDialogOpen(false);
  };

  // Open the strategy management dialog
  const handleOpenStrategyManager = () => {
    setIsStrategyManagerOpen(true);
  };

  // Close the strategy management dialog
  const handleCloseStrategyManager = () => {
    setIsStrategyManagerOpen(false);
  };

  // Load a saved strategy
  const handleLoadStrategy = (strategyId) => {
    // v4.1.4: Fixed — now loads actual strategy nodes/edges from cloud instead of hardcoded template
    loadStrategyFromCloud(strategyId);
    setIsStrategyManagerOpen(false);
  };

  // Delete a strategy
  const handleDeleteStrategy = (strategyId) => {
    // In a real implementation, this would delete from a database
    setStrategies(strategies.filter((s) => s.id !== strategyId));
  };

  // Toggle favorite status for a strategy
  const handleToggleFavorite = (strategyId) => {
    setStrategies(
      strategies.map((s) => {
        if (s.id === strategyId) {
          return { ...s, favorite: !s.favorite };
        }
        return s;
      }),
    );
  };

  // Open the save dialog
  const handleOpenSaveDialog = () => {
    setSaveDialogOpen(true);
  };

  // Close the save dialog
  const handleCloseSaveDialog = () => {
    setSaveDialogOpen(false);
  };

  // Save the current strategy
  const handleSaveStrategy = async () => {
    // Use the cloud save function for persistent storage
    await saveStrategyToCloud();
  };

  // Keyboard shortcuts are handled by the useCallback version of handleKeyDown defined earlier

  // Handle adding a tag
  const handleAddTag = (tag) => {
    if (tag && !strategyTags.includes(tag)) {
      setStrategyTags([...strategyTags, tag]);
    }
  };

  // Handle removing a tag
  const handleDeleteTag = (tagToDelete) => {
    setStrategyTags(strategyTags.filter((tag) => tag !== tagToDelete));
  };

  // Handle template selection

  // Handle template selection
  const handleSelectTemplate = (templateId) => {
    setSelectedTemplate(templateId);
  };

  // Setup functions for API polling
  const setupSocialMediaPolling = (apiId) => {
    // For demonstration purposes
    const interval = setInterval(() => {
      console.log(`Polling ${apiId} for new social media data...`);
      // This would fetch new data from the API
    }, 60000); // Poll every minute

    // Store the interval ID if you need to clear it later
    return interval;
  };

  const setupMarketDataPolling = (apiId) => {
    // For demonstration purposes
    const interval = setInterval(() => {
      console.log(`Polling ${apiId} for updated market data...`);
      // This would fetch new data from the API
    }, 120000); // Poll every 2 minutes

    // Store the interval ID if you need to clear it later
    return interval;
  };

  const setupCryptoAnalyticsPolling = (apiId) => {
    // For demonstration purposes
    const interval = setInterval(() => {
      console.log(`Polling ${apiId} for blockchain analytics data...`);
      // This would fetch new on-chain data from the crypto APIs
    }, 90000); // Poll every 1.5 minutes

    // Store the interval ID if you need to clear it later
    return interval;
  };

  // Communicate with the user API to activate API connections
  const activateAPIConnection = async (connectionType, apiId) => {
    // Create a meaningful connection key for the API
    const connectionKey = `${connectionType}_${apiId}`;
    const timestamp = new Date().toISOString();

    try {
      console.log(
        `Activating ${connectionType} API: ${apiId} with key: ${connectionKey}`,
      );

      // For demo purposes, we'll simulate the API call
      // In a real implementation, this would be an actual API call using fetch or axios
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Store the connection in activeConnections for node communication
      const activeConnections = JSON.parse(
        localStorage.getItem("activeConnections") || "{}",
      );
      activeConnections[connectionKey] = {
        type: connectionType,
        id: apiId,
        activatedAt: timestamp,
        status: "connected",
      };
      localStorage.setItem(
        "activeConnections",
        JSON.stringify(activeConnections),
      );

      // Log the success message
      console.log(
        `Successfully activated ${connectionType} API: ${apiId} at ${timestamp}`,
      );

      // Set up periodic data check for activated connections
      let pollingIntervalId;
      if (connectionType === "social_media") {
        pollingIntervalId = setupSocialMediaPolling(apiId);
        // Store polling interval ID for later cleanup if needed
        activeConnections[connectionKey].pollingIntervalId = pollingIntervalId;
      } else if (connectionType === "market_data") {
        pollingIntervalId = setupMarketDataPolling(apiId);
        // Store polling interval ID for later cleanup if needed
        activeConnections[connectionKey].pollingIntervalId = pollingIntervalId;
      } else if (connectionType === "crypto_analytics") {
        pollingIntervalId = setupCryptoAnalyticsPolling(apiId);
        // Store polling interval ID for later cleanup if needed
        activeConnections[connectionKey].pollingIntervalId = pollingIntervalId;
      }

      // Register the API with node system for data flow
      registerApiWithNodes(connectionKey, connectionType, apiId);

      return {
        success: true,
        message: `Successfully activated ${apiId}`,
        connectionKey,
      };
    } catch (error) {
      console.error(`Error activating ${connectionType} API: ${apiId}`, error);
      return { success: false, message: error.message };
    }
  };

  // Register API with node system for data flow
  const registerApiWithNodes = (connectionKey, connectionType, apiId) => {
    console.log(
      `Registering ${connectionType} API: ${apiId} with nodes using key ${connectionKey}`,
    );
    // This function would communicate with the node system to register this API as a data source
    // For now, we'll just log it, but in a real implementation this would connect to the node processor

    // Create a new node or update existing node for this API
    const existingNodeIndex = nodes.findIndex(
      (node) => node.data?.apiConnection?.connectionKey === connectionKey,
    );

    if (existingNodeIndex >= 0) {
      // Update existing node
      const updatedNodes = [...nodes];
      updatedNodes[existingNodeIndex] = {
        ...updatedNodes[existingNodeIndex],
        data: {
          ...updatedNodes[existingNodeIndex].data,
          apiConnection: {
            connectionKey,
            type: connectionType,
            id: apiId,
            status: "connected",
          },
        },
      };
      setNodes(updatedNodes);
    } else {
      // For demo only: If needed, we could create a new node for this API connection
      console.log(`API ${apiId} ready to be used by nodes`);
    }
  };

  // Test an API connection before activating it
  const testApiConnection = async (connectionType, apiId, credentials) => {
    console.log(`Testing ${connectionType} API connection: ${apiId}`);

    try {
      // In a real implementation, this would make a lightweight call to verify credentials
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Simulate a successful test
      const success = Math.random() > 0.2; // 80% chance of success for demo

      if (success) {
        return { success: true, message: `Successfully connected to ${apiId}` };
      } else {
        throw new Error(
          "Authentication failed. Please check your API credentials.",
        );
      }
    } catch (error) {
      console.error(`Error testing ${connectionType} API: ${apiId}`, error);
      return { success: false, message: error.message };
    }
  };

  // Call testApiConnection from UI when testing connections before activating them
  // This function is used in the UI when the test button is clicked
  window.testStrategyApiConnection = testApiConnection;

  return (
    <Box
      sx={{ height: { xs: 'auto', md: "calc(100vh - 140px)" }, minHeight: { xs: 'calc(100vh - 140px)' }, pb: { xs: 2, md: 0 }, overflow: 'auto', display: 'flex', flexDirection: 'column' }}
      className="strategy-builder"
      tabIndex={-1}
      ref={reactFlowWrapper}
    >
      {/* Header */}
      <Box
        sx={{
          mb: 1, p: { xs: 1.5, sm: 2 }, borderRadius: 3,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
          background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <AccountTreeIcon sx={{ fontSize: { xs: 22, sm: 28 }, mr: 0.5, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.3rem' }, color: 'white' }}>
            Strategy Builder
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<CreateNewFolderIcon />}
            onClick={handleOpenTemplateDialog}
            size="small"
          >
            New
          </Button>
          <Button
            variant="outlined"
            startIcon={<FolderOpenIcon />}
            onClick={handleOpenStrategyManager}
            size="small"
          >
            Open
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadStrategiesFromCloud}
            disabled={isLoadingStrategies}
            size="small"
          >
            {isLoadingStrategies ? "Loading..." : "Refresh"}
          </Button>
          <TextField
            variant="outlined"
            size="small"
            label="Strategy Name"
            value={strategyName}
            onChange={(e) => setStrategyName(e.target.value)}
            sx={{ width: { xs: 120, sm: 160, md: 200 } }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={
              isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CloudUploadIcon />
              )
            }
            onClick={saveStrategyToCloud}
            disabled={isSaving}
            size="small"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
          {!isRunning ? (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={runStrategy}
              disabled={!currentStrategyId}
              size="small"
            >
              Run
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              startIcon={<CircularProgress size={16} color="inherit" />}
              onClick={stopStrategy}
              size="small"
            >
              Stop
            </Button>
          )}
          {/* Toggle Status Panel Button */}
          {currentRunId && (
            <Button
              variant={showRunPanel ? "contained" : "outlined"}
              color="info"
              size="small"
              onClick={() => setShowRunPanel(!showRunPanel)}
            >
              {showRunPanel ? "Hide" : "Status"}
            </Button>
          )}
          {currentStrategyId && (
            <Chip
              label={`ID: ${currentStrategyId.slice(0, 8)}...`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}

          {/* Schedule, Exit Mode, and AI Interval buttons removed - these settings are now inside the Scheduler node */}

          {/* Paper Trading Toggle */}
          <Tooltip
            title={
              isPaperTrading
                ? "Paper Trading Mode: All trades are simulated. No real money is used."
                : "⚠️ LIVE TRADING: Real money will be used for trades!"
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                ml: { xs: 0, sm: 2 },
                p: { xs: 0.5, sm: 1 },
                borderRadius: 2,
                bgcolor: isPaperTrading ? "success.light" : "error.light",
                border: isPaperTrading ? "2px solid green" : "2px solid red",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  fontWeight: "bold",
                  color: isPaperTrading ? "success.dark" : "error.dark",
                }}
              >
                {isPaperTrading ? <><EditNoteIcon sx={{ fontSize: 16, mr: 0.5 }} />Paper</> : <><AccountBalanceWalletIcon sx={{ fontSize: 16, mr: 0.5 }} />LIVE</>}
              </Typography>
              <Switch
                checked={!isPaperTrading}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Switching to live trading - show confirmation
                    if (
                      window.confirm(
                        "⚠️ WARNING: You are about to enable LIVE TRADING!\n\n" +
                          "This will use REAL MONEY for all trades.\n\n" +
                          "Are you absolutely sure you want to continue?",
                      )
                    ) {
                      setIsPaperTrading(false);
                    }
                  } else {
                    setIsPaperTrading(true);
                  }
                }}
                color="error"
                size="small"
              />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Live Run Status Panel */}
      {showRunPanel && (
        <Paper
          elevation={3}
          sx={{
            position: "fixed",
            right: 0,
            top: 64,
            width: { xs: '100vw', sm: 320, md: 350 },
            height: "calc(100vh - 64px)",
            zIndex: 1200,
            overflowY: "auto",
            borderLeft: { xs: 'none', sm: "2px solid" },
            borderColor: isRunning ? "success.main" : runStatus === "completed" ? "info.main" : runStatus === "failed" ? "error.main" : "grey.400",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">
                {isRunning ? "🔄 Running" : runStatus === "completed" ? "✅ Completed" : runStatus === "failed" ? "❌ Failed" : "📊 Run Status"}
              </Typography>
              <IconButton size="small" onClick={() => setShowRunPanel(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Progress */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Progress: {runDetails.completedNodes}/{runDetails.totalNodes} nodes
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={runDetails.totalNodes > 0 ? (runDetails.completedNodes / runDetails.totalNodes) * 100 : 0}
                color={isRunning ? "primary" : runStatus === "completed" ? "success" : "error"}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>

            {/* Current Node */}
            {runDetails.currentNode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Current:</strong> {runDetails.currentNode}
                </Typography>
              </Alert>
            )}

            {/* Run ID */}
            {currentRunId && (
              <Box sx={{ mb: 2, p: 1, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1 }}>
                <Typography variant="caption" color="text.primary">
                  Run ID: {currentRunId.slice(0, 8)}...
                </Typography>
                <br />
                <Typography variant="caption" color="text.primary">
                  Started: {runDetails.startTime ? new Date(runDetails.startTime).toLocaleTimeString() : "N/A"}
                </Typography>
              </Box>
            )}

            {/* Signals - Scrollable list with action buttons */}
            {runDetails.signals.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    <TrendingUpIcon sx={{ fontSize: 18, mr: 0.5 }} /> Signals ({(() => {
                      const validSignals = runDetails.signals.filter(s => s.direction?.toLowerCase() === 'buy' || s.direction?.toLowerCase() === 'sell');
                      if (!runDetails.hideOpenSymbols) return validSignals.length;
                      // Use allOpenPositions (fetched from all runs) for accurate filtering
                      const openPositions = runDetails.allOpenPositions || [];
                      const openSymbols = new Set(openPositions.map(p => p.symbol));
                      const filtered = validSignals.filter(s => !openSymbols.has(s.symbol?.replace('/', '-')));
                      return `${filtered.length}/${validSignals.length}`;
                    })()} found)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={runDetails.hideOpenSymbols || false}
                        onChange={(e) => setRunDetails(prev => ({ ...prev, hideOpenSymbols: e.target.checked }))}
                        color="info"
                      />
                    }
                    label={<Typography variant="caption">Hide Open</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Box>
                
                {/* Global Position Size Controls */}
                <Box sx={{ 
                  mb: 1.5, 
                  p: 1, 
                  bgcolor: 'rgba(33, 150, 243, 0.1)', 
                  borderRadius: 1,
                  border: '1px solid rgba(33, 150, 243, 0.3)'
                }}>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    <AttachMoneyIcon sx={{ fontSize: 16, mr: 0.5 }} /> Position Size (Apply to All)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      label="$ Value"
                      type="number"
                      value={runDetails.globalPositionSize?.usdValue || 100}
                      onChange={(e) => {
                        const newValue = parseFloat(e.target.value) || 0;
                        setRunDetails(prev => ({
                          ...prev,
                          globalPositionSize: { ...prev.globalPositionSize, usdValue: newValue },
                        }));
                      }}
                      sx={{ width: 90 }}
                      InputProps={{ sx: { fontSize: '0.75rem' } }}
                      InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                    />
                    <TextField
                      size="small"
                      label="% Budget"
                      type="number"
                      value={runDetails.globalPositionSize?.budgetPercent || 5}
                      onChange={(e) => {
                        const newPercent = parseFloat(e.target.value) || 0;
                        setRunDetails(prev => ({
                          ...prev,
                          globalPositionSize: { ...prev.globalPositionSize, budgetPercent: newPercent },
                        }));
                      }}
                      sx={{ width: 80 }}
                      InputProps={{ sx: { fontSize: '0.75rem' } }}
                      InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
                    />
                    <Button 
                      size="small" 
                      variant="contained" 
                      color="primary"
                      onClick={() => {
                        const globalSize = runDetails.globalPositionSize || { usdValue: 100, budgetPercent: 5 };
                        setRunDetails(prev => ({
                          ...prev,
                          signalSizes: prev.signals.map(() => ({ ...globalSize })),
                        }));
                      }}
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Apply to All
                    </Button>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  maxHeight: 280, 
                  overflowY: 'auto', 
                  pr: 1,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 3 },
                }}>
                  {[...runDetails.signals]
                    .filter(s => s.direction?.toLowerCase() === 'buy' || s.direction?.toLowerCase() === 'sell')
                    .filter(s => {
                      // If hideOpenSymbols is enabled, filter out symbols that have open positions across ALL runs
                      if (!runDetails.hideOpenSymbols) return true;
                      const signalSymbol = s.symbol?.replace('/', '-');
                      // Use allOpenPositions (fetched from all runs) instead of just current run positions
                      const openPositions = runDetails.allOpenPositions || [];
                      return !openPositions.some(p => p.symbol === signalSymbol);
                    })
                    .sort((a, b) => (b.confidence || 0) - (a.confidence || 0))
                    .map((signal, idx) => {
                      const originalIdx = runDetails.signals.indexOf(signal);
                      return (
                        <SignalCard 
                          key={originalIdx}
                          signal={signal}
                          idx={originalIdx}
                          currentRunId={currentRunId}
                          setRunDetails={setRunDetails}
                          positionSize={runDetails.signalSizes?.[originalIdx] || runDetails.globalPositionSize || { usdValue: 100, budgetPercent: 5 }}
                          onSizeChange={(signalIdx, newSize) => {
                            setRunDetails(prev => {
                              const newSizes = [...(prev.signalSizes || prev.signals.map(() => prev.globalPositionSize || { usdValue: 100, budgetPercent: 5 }))];
                              newSizes[signalIdx] = newSize;
                              return { ...prev, signalSizes: newSizes };
                            });
                          }}
                        />
                      );
                    })}
                </Box>
              </Box>
            )}

            {/* Trades */}
            {runDetails.trades.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom><AttachMoneyIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Trades</Typography>
                {runDetails.trades.slice(-5).map((trade, idx) => (
                  <Box key={idx} sx={{ p: 1, mb: 1, bgcolor: trade.side === "buy" ? "success.light" : "error.light", borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>{trade.side?.toUpperCase()}</strong> {trade.quantity} {trade.symbol}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      @ ${trade.price} | {trade.status}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Capital Status - Money Management */}
            {runDetails.capitalStatus && runDetails.capitalStatus.settings?.enabled && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.1)', border: '1px solid', borderColor: 'success.main', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                    💰 Capital Management
                  </Typography>
                  <Chip 
                    size="small" 
                    label={`${runDetails.capitalStatus.openPositions}/${runDetails.capitalStatus.maxPositions} positions`}
                    sx={{ bgcolor: 'success.main', color: 'white', height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 80, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      ${runDetails.capitalStatus.totalCapital?.toFixed(0) || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 80, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Deployable</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      ${runDetails.capitalStatus.deployableNow?.toFixed(0) || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 80, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Reserved</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      ${runDetails.capitalStatus.futureReserve?.toFixed(0) || 0}
                    </Typography>
                  </Box>
                  {runDetails.capitalStatus.rescueReserve > 0 && (
                    <Box sx={{ flex: 1, minWidth: 80, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Rescue</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        ${runDetails.capitalStatus.rescueReserve?.toFixed(0) || 0}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Max per trade: ${runDetails.capitalStatus.perTradeMax?.toFixed(0)} | Max per symbol: ${runDetails.capitalStatus.perSymbolMax?.toFixed(0)}
                </Typography>
              </Box>
            )}

            {/* Sound Notification Toggle - Always visible */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Tooltip title={soundMuted ? "Unmute sound notifications" : "Mute sound notifications"}>
                <IconButton size="small" onClick={() => setSoundMuted(!soundMuted)} sx={{ color: soundMuted ? 'text.disabled' : 'info.main' }}>
                  {soundMuted ? <VolumeOffIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color={soundMuted ? 'text.disabled' : 'text.secondary'} sx={{ ml: 0.5 }}>
                {soundMuted ? 'Sound Off' : 'Sound On'}
              </Typography>
            </Box>

            {/* Positions - Clickable to show execution details */}
            {/* Pending Signals (Confirmation Mode) */}
            {pendingSignals.length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255, 152, 0, 0.08)', border: '1px solid', borderColor: 'warning.main', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                    <PendingActionsIcon sx={{ fontSize: 16, mr: 0.5 }} /> Awaiting Confirmation ({pendingSignals.length})
                  </Typography>
                </Box>
                {pendingSignals.map((sig) => (
                  <Box key={sig.id} sx={{ p: 1, mb: 0.5, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1, border: '1px solid', borderColor: sig.direction === 'buy' ? 'success.dark' : 'error.dark' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip
                          label={sig.direction?.toUpperCase()}
                          size="small"
                          color={sig.direction === 'buy' ? 'success' : 'error'}
                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 'bold' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{sig.symbol}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(sig.confidence)}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        @ ${sig.priceAtSignal?.toFixed(sig.priceAtSignal > 100 ? 0 : sig.priceAtSignal > 1 ? 2 : 6)} • {sig.method}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(sig.createdAt).toLocaleTimeString()}
                      </Typography>
                    </Box>
                    {sig.patterns && sig.patterns.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                        {sig.patterns.slice(0, 3).map((p, i) => (
                          <Chip key={i} label={typeof p === 'string' ? p : p.pattern || p.type || 'pattern'} size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                        ))}
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleConfirmSignal(sig.id)}
                        sx={{ flex: 1, fontSize: '0.7rem', py: 0.3 }}
                      >
                        Confirm
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleRejectSignal(sig.id)}
                        sx={{ flex: 1, fontSize: '0.7rem', py: 0.3 }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {runDetails.positions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2"><BarChartIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Open Positions</Typography>
                  {runDetails.selectedPosition && (
                    <Button 
                      size="small" 
                      variant="text" 
                      onClick={() => setRunDetails(prev => ({ ...prev, selectedPosition: null }))}
                    >
                      Show All
                    </Button>
                  )}
                </Box>
                {runDetails.positions.filter(p => p.status === 'open').map((pos, idx) => (
                  <React.Fragment key={idx}>
                    <Box 
                      sx={{ 
                        p: 1, 
                        mb: runDetails.selectedPosition?.id === pos.id ? 0 : 1, 
                        border: "2px solid", 
                        borderColor: runDetails.selectedPosition?.id === pos.id 
                          ? "primary.main" 
                          : (pos.pnl >= 0 ? "success.main" : "error.main"), 
                        borderRadius: runDetails.selectedPosition?.id === pos.id ? '4px 4px 0 0' : 1,
                        borderBottom: runDetails.selectedPosition?.id === pos.id ? 'none' : undefined,
                        bgcolor: runDetails.selectedPosition?.id === pos.id 
                          ? "rgba(25, 118, 210, 0.15)" 
                          : "rgba(255,255,255,0.05)",
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                      }}
                      onClick={() => setRunDetails(prev => ({ 
                        ...prev, 
                        selectedPosition: prev.selectedPosition?.id === pos.id ? null : pos 
                      }))}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            <strong>{pos.side?.toUpperCase()}</strong> {pos.quantity?.toFixed(6)} {pos.symbol}
                          </Typography>
                          {pos.isAggregated && (
                            <Chip 
                              size="small" 
                              label={`${pos.tradeCount || 1} trades`}
                              sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'info.main', color: 'white' }}
                            />
                          )}
                          {runDetails.selectedPosition?.id === pos.id && <VisibilityIcon sx={{ fontSize: 14, ml: 0.5 }} />}
                        </Box>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          color="error"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await StrategyEngineService.closePosition(currentRunId, pos.id);
                              setRunDetails(prev => ({
                                ...prev,
                                positions: prev.positions.map(p => 
                                  p.id === pos.id ? { ...p, status: 'closed' } : p
                                ),
                                selectedPosition: null,
                              }));
                            } catch (e) {
                              console.error("Error closing position:", e);
                            }
                          }}
                        >
                          Close
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {pos.isAggregated ? 'Avg ' : ''}Entry: ${(pos.averageEntryPrice || pos.entryPrice)?.toFixed(2)} | Current: ${pos.currentPrice?.toFixed(2)}
                      </Typography>
                      <br />
                      <Typography variant="caption" color={pos.pnl >= 0 ? "success.main" : "error.main"}>
                        P&L: ${pos.pnl?.toFixed(4) || "0.00"} ({pos.pnlPercent?.toFixed(2) || "0.00"}%)
                      </Typography>
                      {pos.dynamicTakeProfit && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'info.main' }}>
                          <GpsFixedIcon sx={{ fontSize: 12, mr: 0.5 }} /> Dynamic TP: {pos.dynamicTakeProfit.toFixed(1)}% | SL: {pos.dynamicStopLoss?.toFixed(1)}%
                        </Typography>
                      )}
                      {pos.rescueEnabled && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'secondary.main' }}>
                          🛟 Rescue: {pos.addsUsed || 0}/{pos.maxAdds || 1} adds
                          {pos.blendedEntryPrice && pos.blendedEntryPrice !== pos.initialEntryPrice && (
                            <span style={{ marginLeft: 4 }}>
                              | Entry: ${pos.initialEntryPrice?.toFixed(2)} → ${pos.blendedEntryPrice?.toFixed(2)}
                            </span>
                          )}
                        </Typography>
                      )}
                    </Box>
                    {/* Inline Position Details - appears right under selected position */}
                    {runDetails.selectedPosition?.id === pos.id && (
                      <Box sx={{ mb: 1, p: 1.5, bgcolor: 'rgba(25, 118, 210, 0.1)', border: '2px solid', borderColor: 'primary.main', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                        {/* TradingView Mini Chart */}
                        <Box sx={{ mb: 1.5, borderRadius: 1, overflow: 'hidden', height: 200 }}>
                          <iframe
                            src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&symbol=${getTradingViewSymbol(pos.symbol)}&interval=15&hidesidetoolbar=1&symboledit=1&saveimage=0&toolbarbg=f1f3f6&studies=RSI@tv-basicstudies&theme=dark&style=1&timezone=exchange&withdateranges=0&showpopupbutton=0&studies_overrides={}&overrides={}&enabled_features=[]&disabled_features=[]&locale=en&utm_source=&utm_medium=widget&utm_campaign=chart`}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title={`${pos.symbol} Chart`}
                            allowFullScreen
                          />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ bgcolor: pos.side === 'buy' ? 'success.main' : 'error.main', px: 1, py: 0.25, borderRadius: 1, color: 'white' }}>
                            {pos.side === 'buy' ? '🟢 LONG' : '🔴 SHORT'}
                          </Typography>
                          <Typography variant="caption" sx={{ bgcolor: 'grey.700', px: 1, py: 0.25, borderRadius: 1 }}>
                            Qty: {pos.quantity}
                          </Typography>
                          <Typography variant="caption" sx={{ bgcolor: 'info.main', px: 1, py: 0.25, borderRadius: 1 }}>
                            Entry: ${pos.entryPrice?.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" sx={{ bgcolor: 'warning.main', px: 1, py: 0.25, borderRadius: 1, color: 'black' }}>
                            Current: ${pos.currentPrice?.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ 
                            bgcolor: pos.pnl >= 0 ? 'success.main' : 'error.main', 
                            px: 1, py: 0.25, borderRadius: 1 
                          }}>
                            P&L: ${pos.pnl?.toFixed(4)} ({pos.pnlPercent?.toFixed(2)}%)
                          </Typography>
                          {pos.confidence && (
                            <Typography variant="caption" sx={{ bgcolor: 'purple', px: 1, py: 0.25, borderRadius: 1 }}>
                              Confidence: {(pos.confidence * 100).toFixed(0)}%
                            </Typography>
                          )}
                          {pos.isAggregated && (
                            <Typography variant="caption" sx={{ bgcolor: 'info.main', px: 1, py: 0.25, borderRadius: 1 }}>
                              Aggregated ({pos.tradeCount} trades)
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Logical Trades Section - for aggregated positions */}
                        {pos.logicalTrades && pos.logicalTrades.length > 0 && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: '1px dashed', borderColor: pos.addsUsed > 0 ? 'secondary.main' : 'grey.600' }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', color: pos.addsUsed > 0 ? 'secondary.main' : 'info.main', display: 'block', mb: 0.5 }}>
                              {pos.addsUsed > 0 ? '🛟 Position Entries + Rescue Adds' : '📊 Individual Entries (Netting Mode)'}
                            </Typography>
                            {pos.logicalTrades.map((trade, tIdx) => {
                              const isRescue = trade.method === 'rescue_scale_in';
                              return (
                              <Box key={tIdx} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25, borderBottom: tIdx < pos.logicalTrades.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none', bgcolor: isRescue ? 'rgba(156, 39, 176, 0.1)' : 'transparent', px: 0.5, borderRadius: 0.5 }}>
                                <Typography variant="caption" sx={{ color: isRescue ? 'secondary.light' : 'text.secondary' }}>
                                  {isRescue ? `🛟 Rescue #${tIdx}` : `#${tIdx + 1} Entry`}: {trade.quantity?.toFixed(6)} @ ${trade.entryPrice?.toFixed(2)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: isRescue ? 'secondary.light' : 'text.secondary' }}>
                                  {isRescue ? 'RESCUE' : (trade.confidence ? `${(trade.confidence * 100).toFixed(0)}%` : '')} • {new Date(trade.openedAt).toLocaleTimeString()}
                                </Typography>
                              </Box>
                            );})}
                            {pos.isAggregated && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'warning.main', fontStyle: 'italic' }}>
                                Broker merged these into one position
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {/* AI Dynamic Stops Section */}
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(25, 118, 210, 0.1)', border: '1px solid', borderColor: 'info.main', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                              <SmartToyIcon sx={{ fontSize: 14, mr: 0.5 }} /> AI Pro Trader
                            </Typography>
                            <Chip 
                              size="small" 
                              label={pos.autoTpSlEnabled !== false ? "AUTO" : "MANUAL"} 
                              sx={{ bgcolor: pos.autoTpSlEnabled !== false ? 'success.main' : 'warning.main', color: 'white', height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                            {/* Take Profit Box with Toggle */}
                            <Box sx={{ flex: 1, p: 1, bgcolor: 'rgba(46, 125, 50, 0.2)', borderRadius: 1, textAlign: 'center', position: 'relative' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Take Profit</Typography>
                                <Switch
                                  size="small"
                                  checked={pos.autoTpEnabled !== false}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await StrategyEngineService.updatePositionSettings(currentRunId, pos.id, { autoTpEnabled: e.target.checked });
                                      setRunDetails(prev => ({
                                        ...prev,
                                        positions: prev.positions.map(p => 
                                          p.id === pos.id ? { ...p, autoTpEnabled: e.target.checked } : p
                                        ),
                                        selectedPosition: prev.selectedPosition?.id === pos.id 
                                          ? { ...prev.selectedPosition, autoTpEnabled: e.target.checked } 
                                          : prev.selectedPosition,
                                      }));
                                    } catch (err) { console.error('Failed to update TP setting:', err); }
                                  }}
                                  sx={{ 
                                    p: 0, 
                                    '& .MuiSwitch-switchBase': { p: '2px' },
                                    '& .MuiSwitch-thumb': { width: 12, height: 12 },
                                    '& .MuiSwitch-track': { borderRadius: 10 },
                                    width: 32, height: 18
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                {pos.dynamicTakeProfit?.toFixed(1) || '2.5'}%
                              </Typography>
                              {/* Show exact TP price */}
                              <Typography variant="caption" sx={{ display: 'block', color: 'success.light', fontWeight: 'bold', mt: 0.5 }}>
                                ${(() => {
                                  const tp = pos.dynamicTakeProfit || 2.5;
                                  const entry = pos.entryPrice || 0;
                                  // For BUY: TP is above entry, For SELL: TP is below entry
                                  return pos.side === 'buy' 
                                    ? (entry * (1 + tp / 100)).toFixed(2)
                                    : (entry * (1 - tp / 100)).toFixed(2);
                                })()}
                              </Typography>
                            </Box>
                            {/* Stop Loss Box with Toggle */}
                            <Box sx={{ flex: 1, p: 1, bgcolor: 'rgba(211, 47, 47, 0.2)', borderRadius: 1, textAlign: 'center', position: 'relative' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Stop Loss</Typography>
                                <Switch
                                  size="small"
                                  checked={pos.autoSlEnabled !== false}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await StrategyEngineService.updatePositionSettings(currentRunId, pos.id, { autoSlEnabled: e.target.checked });
                                      setRunDetails(prev => ({
                                        ...prev,
                                        positions: prev.positions.map(p => 
                                          p.id === pos.id ? { ...p, autoSlEnabled: e.target.checked } : p
                                        ),
                                        selectedPosition: prev.selectedPosition?.id === pos.id 
                                          ? { ...prev.selectedPosition, autoSlEnabled: e.target.checked } 
                                          : prev.selectedPosition,
                                      }));
                                    } catch (err) { console.error('Failed to update SL setting:', err); }
                                  }}
                                  sx={{ 
                                    p: 0, 
                                    '& .MuiSwitch-switchBase': { p: '2px' },
                                    '& .MuiSwitch-thumb': { width: 12, height: 12 },
                                    '& .MuiSwitch-track': { borderRadius: 10 },
                                    width: 32, height: 18
                                  }}
                                />
                              </Box>
                              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                {pos.dynamicStopLoss?.toFixed(1) || '2.0'}%
                              </Typography>
                              {/* Show exact SL price */}
                              <Typography variant="caption" sx={{ display: 'block', color: 'error.light', fontWeight: 'bold', mt: 0.5 }}>
                                ${(() => {
                                  const sl = pos.dynamicStopLoss || 2.0;
                                  const entry = pos.entryPrice || 0;
                                  // For BUY: SL is below entry, For SELL: SL is above entry
                                  return pos.side === 'buy' 
                                    ? (entry * (1 - sl / 100)).toFixed(2)
                                    : (entry * (1 + sl / 100)).toFixed(2);
                                })()}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {pos.lastAdjustment ? (
                            <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                              <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', fontWeight: 'bold' }}>
                                <EditNoteIcon sx={{ fontSize: 14, mr: 0.5 }} /> Last AI Adjustment:
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                {pos.lastAdjustment.adjustment}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                {pos.lastAdjustment.timestamp ? new Date(pos.lastAdjustment.timestamp).toLocaleString() : 'Recently'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                              <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} /> Waiting for first AI analysis...
                            </Typography>
                          )}
                          
                          {/* Technical Indicators */}
                          {(pos.lastRsi || pos.lastMacdSignal) && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {pos.lastRsi && (
                                <Chip 
                                  size="small" 
                                  label={`RSI: ${pos.lastRsi?.toFixed(1)}`}
                                  sx={{ 
                                    bgcolor: pos.lastRsi > 70 ? 'error.main' : pos.lastRsi < 30 ? 'success.main' : 'grey.600',
                                    color: 'white',
                                    height: 20,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                              {pos.lastMacdSignal && (
                                <Chip 
                                  size="small" 
                                  label={`MACD: ${pos.lastMacdSignal}`}
                                  sx={{ 
                                    bgcolor: pos.lastMacdSignal === 'bullish' ? 'success.main' : 'error.main',
                                    color: 'white',
                                    height: 20,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                        
                        {/* Rescue Scale-In Section */}
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'rgba(156, 39, 176, 0.1)', border: '1px solid', borderColor: 'secondary.main', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                              🛟 Rescue Scale-In
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                size="small" 
                                label={pos.rescueEnabled ? "ENABLED" : "DISABLED"} 
                                sx={{ bgcolor: pos.rescueEnabled ? 'secondary.main' : 'grey.600', color: 'white', height: 18, fontSize: '0.65rem' }}
                              />
                              <Switch
                                size="small"
                                checked={pos.rescueEnabled || false}
                                onChange={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await StrategyEngineService.updatePositionSettings(currentRunId, pos.id, { rescueEnabled: e.target.checked });
                                    setRunDetails(prev => ({
                                      ...prev,
                                      positions: prev.positions.map(p => 
                                        p.id === pos.id ? { ...p, rescueEnabled: e.target.checked } : p
                                      ),
                                      selectedPosition: prev.selectedPosition?.id === pos.id 
                                        ? { ...prev.selectedPosition, rescueEnabled: e.target.checked } 
                                        : prev.selectedPosition,
                                    }));
                                  } catch (err) { console.error('Failed to update rescue setting:', err); }
                                }}
                                sx={{ 
                                  p: 0, 
                                  '& .MuiSwitch-switchBase': { p: '2px' },
                                  '& .MuiSwitch-thumb': { width: 12, height: 12 },
                                  '& .MuiSwitch-track': { borderRadius: 10 },
                                  width: 32, height: 18
                                }}
                              />
                            </Box>
                          </Box>
                          
                          {pos.rescueEnabled && (
                            <>
                              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <Box sx={{ flex: 1, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                                  <Typography variant="caption" color="text.secondary">Adds Used</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: pos.addsUsed > 0 ? 'warning.main' : 'text.primary' }}>
                                    {pos.addsUsed || 0} / {pos.maxAdds || 1}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                                  <Typography variant="caption" color="text.secondary">Initial Entry</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    ${pos.initialEntryPrice?.toFixed(2) || pos.entryPrice?.toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1, p: 0.75, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1, textAlign: 'center' }}>
                                  <Typography variant="caption" color="text.secondary">Blended Entry</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: pos.blendedEntryPrice !== pos.initialEntryPrice ? 'info.main' : 'text.primary' }}>
                                    ${pos.blendedEntryPrice?.toFixed(2) || pos.entryPrice?.toFixed(2)}
                                  </Typography>
                                </Box>
                              </Box>
                              
                              {pos.lastRescueAt && (
                                <Box sx={{ p: 0.75, bgcolor: 'rgba(156, 39, 176, 0.15)', borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ display: 'block', color: 'secondary.light' }}>
                                    Last Rescue: {new Date(pos.lastRescueAt).toLocaleString()}
                                  </Typography>
                                  {pos.rescueReason && (
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                                      {pos.rescueReason}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
                                ⚠️ Only adds if thesis valid & in volatility drawdown
                              </Typography>
                            </>
                          )}
                          
                          {!pos.rescueEnabled && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                              Enable to allow position repair when in temporary loss but thesis still valid
                            </Typography>
                          )}
                        </Box>
                        
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                          Opened: {new Date(pos.openedAt).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </React.Fragment>
                ))}
                {runDetails.positions.filter(p => p.status === 'closed').length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    + {runDetails.positions.filter(p => p.status === 'closed').length} closed positions
                  </Typography>
                )}
              </Box>
            )}

            {/* Node Results - Improved UI */}
            {runDetails.nodeResults.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <SettingsIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Node Execution
                </Typography>
                <Box sx={{ 
                  maxHeight: 300, 
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 3 },
                }}>
                  {runDetails.nodeResults.map((result, idx) => {
                    // Parse output for better display
                    const output = result.output || {};
                    const analysis = output.analysis || output.signal || {};
                    const symbol = analysis.symbol || output.symbol || '';
                    const signal = analysis.signal || output.direction || analysis.direction || '';
                    const price = analysis.currentPrice || analysis.price || output.price || '';
                    const rsi = analysis.rsi || output.rsi || '';
                    const confidence = analysis.confidence || output.confidence || '';
                    const macd = analysis.macd || output.macd || '';
                    
                    return (
                      <Box 
                        key={idx} 
                        sx={{ 
                          p: 1.5, 
                          mb: 1, 
                          bgcolor: result.success ? "rgba(46, 125, 50, 0.15)" : "rgba(211, 47, 47, 0.15)",
                          border: '1px solid',
                          borderColor: result.success ? "success.main" : "error.main",
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: result.success ? 'success.main' : 'error.main' }}>
                            {result.nodeType}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            px: 1, 
                            py: 0.25, 
                            borderRadius: 1, 
                            bgcolor: result.success ? 'success.main' : 'error.main',
                            color: 'white',
                          }}>
                            {result.success ? "✓ Success" : "✗ Failed"}
                          </Typography>
                        </Box>
                        
                        {/* Show parsed data nicely */}
                        {symbol && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {symbol && (
                                <Typography variant="caption" sx={{ bgcolor: 'primary.main', px: 1, py: 0.25, borderRadius: 1 }}>
                                  📊 {symbol}
                                </Typography>
                              )}
                              {signal && signal !== 'HOLD' && (
                                <Typography variant="caption" sx={{ 
                                  bgcolor: signal === 'BUY' || signal === 'buy' ? 'success.main' : 'error.main', 
                                  px: 1, py: 0.25, borderRadius: 1 
                                }}>
                                  {signal === 'BUY' || signal === 'buy' ? '🟢' : '🔴'} {signal.toUpperCase()}
                                </Typography>
                              )}
                              {price && (
                                <Typography variant="caption" sx={{ bgcolor: 'grey.700', px: 1, py: 0.25, borderRadius: 1 }}>
                                  💰 ${typeof price === 'number' ? price.toFixed(2) : price}
                                </Typography>
                              )}
                              {rsi && (
                                <Typography variant="caption" sx={{ 
                                  bgcolor: rsi > 70 ? 'error.main' : rsi < 30 ? 'success.main' : 'grey.600', 
                                  px: 1, py: 0.25, borderRadius: 1 
                                }}>
                                  RSI: {typeof rsi === 'number' ? rsi.toFixed(1) : rsi}
                                </Typography>
                              )}
                              {confidence && (
                                <Typography variant="caption" sx={{ bgcolor: 'info.main', px: 1, py: 0.25, borderRadius: 1 }}>
                                  🎯 {(confidence * 100).toFixed(0)}%
                                </Typography>
                              )}
                            </Box>
                            {macd && typeof macd === 'object' && (
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                                MACD: {macd.histogram > 0 ? '📈 Bullish' : '📉 Bearish'}
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {/* Fallback for other node types */}
                        {!symbol && output.message && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                            {output.message}
                          </Typography>
                        )}
                        {!symbol && output.stored !== undefined && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                            💾 Stored: {output.stored ? 'Yes' : 'No'} | Type: {output.storageType || 'N/A'}
                          </Typography>
                        )}
                        {!symbol && output.executed !== undefined && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                            ✅ Condition: {output.executed ? 'Passed' : 'Failed'}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Errors */}
            {runDetails.errors.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>⚠️ Errors</Typography>
                {runDetails.errors.map((err, idx) => (
                  <Alert key={idx} severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2">{err}</Typography>
                  </Alert>
                ))}
              </Box>
            )}

            {/* Last Update */}
            <Typography variant="caption" color="text.secondary">
              Last update: {runDetails.lastUpdate ? new Date(runDetails.lastUpdate).toLocaleTimeString() : "N/A"}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Main content */}
      <Grid container spacing={1} sx={{ flex: 1, minHeight: { xs: "calc(100vh - 200px)", md: 0 } }}>
        {/* Left sidebar - Node palette */}
        <Grid item xs={12} sm={4} md={2} sx={{ order: { xs: 2, md: 1 }, maxHeight: { xs: 'none', md: '100%' }, overflow: 'auto' }}>
          <Paper sx={{ height: "100%", p: { xs: 1, md: 2 }, overflowY: "auto" }}>
            <Typography variant="h6" gutterBottom>
              Agent Nodes
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Drag and drop or click to add nodes to your strategy
              </Typography>
            </Box>

            {nodeTemplates.map((template) => (
              <Button
                key={template.type}
                variant="outlined"
                fullWidth
                startIcon={template.icon}
                onClick={() => onAddNode(template.type)}
                sx={{
                  justifyContent: "flex-start",
                  mb: 1,
                  borderColor: template.color,
                  "&:hover": {
                    borderColor: template.color,
                    backgroundColor: `${template.color}10`,
                  },
                }}
              >
                {template.label}
              </Button>
            ))}

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="h6">Cloud Strategies</Typography>
              {isLoadingStrategies && <CircularProgress size={16} />}
            </Box>

            <Box sx={{ mb: 2 }}>
              {strategies.length === 0 && !isLoadingStrategies && (
                <Typography variant="caption" color="text.secondary">
                  No strategies saved yet. Create one and click "Save to Cloud".
                </Typography>
              )}
              {strategies.map((strategy) => {
                // Get execution status for this strategy
                const execStatus = executionStatus.strategies?.find(s => s.strategyId === strategy.id);
                const isScheduled = execStatus?.enabled;
                const tradingMode = execStatus?.tradingMode || 'spot';
                const broker = execStatus?.broker || 'kucoin';
                const openPositions = execStatus?.openPositionsCount || 0;
                
                return (
                <Box
                  key={strategy.id}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: "1px solid",
                    borderColor:
                      isScheduled
                        ? "success.main"
                        : currentStrategyId === strategy.id
                        ? "primary.main"
                        : "divider",
                    borderRadius: 1,
                    backgroundColor:
                      isScheduled
                        ? "rgba(46, 125, 50, 0.08)"
                        : currentStrategyId === strategy.id
                        ? "action.selected"
                        : "transparent",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "action.hover",
                    },
                  }}
                  onClick={() => loadStrategyFromCloud(strategy.id)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{strategy.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {execStatus && (
                        <Tooltip title={isScheduled ? "Deactivate strategy" : "Activate strategy"}>
                          <Switch
                            size="small"
                            checked={!!isScheduled}
                            onChange={(e) => toggleStrategySchedule(strategy.id, e)}
                            onClick={(e) => e.stopPropagation()}
                            color="success"
                            sx={{ 
                              mr: -0.5,
                              '& .MuiSwitch-switchBase': { padding: '4px' },
                              '& .MuiSwitch-thumb': { width: 14, height: 14 },
                              '& .MuiSwitch-track': { borderRadius: 10 },
                            }}
                          />
                        </Tooltip>
                      )}
                      {isScheduled && (
                        <Chip
                          label="RUNNING"
                          size="small"
                          color="success"
                          sx={{ fontSize: "0.6rem", height: 18, animation: 'pulse 2s infinite' }}
                        />
                      )}
                      <Chip
                        label={strategy.status || "draft"}
                        size="small"
                        color={
                          strategy.status === "active" ? "success" : "default"
                        }
                        sx={{ fontSize: "0.65rem", height: 18 }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Execution Info Row */}
                  {isScheduled && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={broker.toUpperCase()}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.55rem", height: 16 }}
                      />
                      <Chip
                        label={tradingMode.toUpperCase()}
                        size="small"
                        variant="outlined"
                        color={tradingMode === 'spot' ? 'primary' : tradingMode === 'margin' ? 'warning' : 'error'}
                        sx={{ fontSize: "0.55rem", height: 16 }}
                      />
                      {execStatus?.paperTrading && (
                        <Chip
                          label="PAPER"
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={{ fontSize: "0.55rem", height: 16 }}
                        />
                      )}
                      {openPositions > 0 && (
                        <Chip
                          label={`${openPositions} pos`}
                          size="small"
                          variant="filled"
                          color="secondary"
                          sx={{ fontSize: "0.55rem", height: 16 }}
                        />
                      )}
                    </Box>
                  )}
                  
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {strategy.description?.slice(0, 50)}
                    {strategy.description?.length > 50 ? "..." : ""}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Updated: {strategy.lastUpdated}
                  </Typography>
                  <Box sx={{ display: "flex", mt: 1 }}>
                    <Tooltip title="Load Strategy">
                      <IconButton
                        size="small"
                        sx={{ mr: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          loadStrategyFromCloud(strategy.id);
                        }}
                      >
                        <CloudDownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Run Strategy">
                      <IconButton
                        size="small"
                        sx={{ mr: 0.5 }}
                        color="success"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentStrategyId(strategy.id);
                          runStrategy(strategy.id);
                        }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Save as Template">
                      <IconButton
                        size="small"
                        sx={{ mr: 0.5 }}
                        color="warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSaveTemplateStrategyId(strategy.id);
                          setTemplateName(strategy.name);
                          setTemplateDescription(strategy.description || "");
                          setSaveTemplateDialogOpen(true);
                        }}
                      >
                        <BookmarkBorderIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clone Strategy">
                      <IconButton
                        size="small"
                        sx={{ mr: 0.5 }}
                        color="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCloneSourceId(strategy.id);
                          setCloneName(`${strategy.name} (Copy)`);
                          setCloneDialogOpen(true);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Strategy">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteStrategyFromCloud(strategy.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
              })}
            </Box>

            {/* Saved Templates Section */}
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="h6"><LibraryBooksIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />Saved Templates</Typography>
              {isLoadingTemplates && <CircularProgress size={16} />}
            </Box>
            
            <Box sx={{ mb: 1 }}>
              {savedTemplates.length === 0 && !isLoadingTemplates && (
                <Typography variant="caption" color="text.secondary">
                  No templates saved yet. Use <BookmarkBorderIcon sx={{ fontSize: 12 }} /> on a strategy to save it as a template.
                </Typography>
              )}
              {savedTemplates.map((tmpl) => (
                <Box
                  key={tmpl.id}
                  sx={{
                    p: 1,
                    mb: 0.5,
                    border: "1px solid",
                    borderColor: "warning.dark",
                    borderRadius: 1,
                    backgroundColor: "rgba(237, 108, 2, 0.06)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.75rem' }}>
                      <BookmarkIcon sx={{ fontSize: 14, mr: 0.5, color: 'warning.main', verticalAlign: 'text-bottom' }} />
                      {tmpl.name}
                    </Typography>
                    <Chip label={`${tmpl.nodeCount || 0} nodes`} size="small" sx={{ fontSize: "0.55rem", height: 16 }} />
                  </Box>
                  {tmpl.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      {tmpl.description.slice(0, 40)}{tmpl.description.length > 40 ? "..." : ""}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Saved: {tmpl.createdAt?.split("T")[0] || "N/A"}
                  </Typography>
                  <Box sx={{ display: "flex", mt: 0.5, gap: 0.5 }}>
                    <Tooltip title="Load from Template">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                          setLoadTemplateId(tmpl.id);
                          setLoadTemplateName(tmpl.name);
                          setLoadTemplateDialogOpen(true);
                        }}
                      >
                        <CloudDownloadIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Template">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTemplate(tmpl.id, tmpl.name)}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main flow editor */}
        <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 2 }, minHeight: { xs: 400, sm: 500, md: 'auto' } }}>
          <Paper sx={{ height: "100%", position: "relative" }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={customNodesChange}
              onEdgesChange={customEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              nodeTypes={nodeTypes}
              connectionLineType={ConnectionLineType.SmoothStep}
              connectionLineStyle={connectionLineStyle}
              defaultEdgeOptions={defaultEdgeOptions}
              connectionMode={ConnectionMode.LOOSE}
              fitView
            >
              <Controls />
              <MiniMap
                nodeStrokeWidth={3}
                zoomable
                pannable
                nodeBorderRadius={2}
                style={{ backgroundColor: "rgba(240,240,240,0.8)" }}
              />
              <Background color="#f8f8f8" gap={16} size={1} />

              <Panel
                position="top-right"
                style={{ marginTop: 10, marginRight: 10 }}
              >
                <Paper sx={{ p: 1, boxShadow: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveStrategy}
                    disabled={isSaving}
                    sx={{ mr: 1 }}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<PlayArrowIcon />}
                    sx={{ mr: 1 }}
                  >
                    Test
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<UndoIcon />}
                    onClick={handleUndo}
                    disabled={undoStack.length <= 0}
                    title="Undo (Ctrl+Z)"
                  >
                    Undo
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RedoIcon />}
                    onClick={handleRedo}
                    disabled={redoStack.length <= 0}
                    title="Redo (Ctrl+Y)"
                    sx={{ ml: 1 }}
                  >
                    Redo
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleClearCanvas}
                    sx={{ ml: 1 }}
                  >
                    Clear
                  </Button>
                </Paper>
              </Panel>
            </ReactFlow>
          </Paper>
        </Grid>

        {/* Right sidebar - Properties */}
        <Grid item xs={12} sm={8} md={3} sx={{ order: { xs: 3, md: 3 }, maxHeight: { xs: 400, md: 'none' } }}>
          <Paper sx={{ height: "100%", p: { xs: 1, md: 2 }, overflowY: "auto" }}>
            <Typography variant="h6" gutterBottom>
              Strategy Properties
            </Typography>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>General Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <TextField
                    label="Description"
                    multiline
                    rows={2}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    defaultValue="This strategy uses technical analysis to generate trading signals, filters them through risk management rules, and executes trades accordingly."
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Schedule</InputLabel>
                    <Select label="Schedule" defaultValue="daily">
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="manual">Manual Trigger</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <InputLabel>Maximum Risk</InputLabel>
                    <Select label="Maximum Risk" defaultValue="2">
                      <MenuItem value="1">1% of Portfolio</MenuItem>
                      <MenuItem value="2">2% of Portfolio</MenuItem>
                      <MenuItem value="5">5% of Portfolio</MenuItem>
                      <MenuItem value="10">10% of Portfolio</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Error Handling</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl fullWidth margin="normal">
                  <InputLabel>On Error</InputLabel>
                  <Select label="On Error" defaultValue="stop">
                    <MenuItem value="stop">Stop Execution</MenuItem>
                    <MenuItem value="continue">Continue Execution</MenuItem>
                    <MenuItem value="retry">Retry (max 3 times)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Notifications</InputLabel>
                  <Select label="Notifications" defaultValue="all">
                    <MenuItem value="all">All Events</MenuItem>
                    <MenuItem value="errors">Errors Only</MenuItem>
                    <MenuItem value="trades">Trades Only</MenuItem>
                    <MenuItem value="none">No Notifications</MenuItem>
                  </Select>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Export Options</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Export as JSON
                </Button>
                <Button variant="outlined" startIcon={<CodeIcon />} fullWidth>
                  Export to Marketplace
                </Button>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>

      {/* Enhanced Node Settings Dialog with AI Integration */}
      <Dialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="node-settings-dialog-title"
        disableScrollLock
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle id="node-settings-dialog-title">Node Settings</DialogTitle>
        <DialogContent>
          {selectedNode && (
            <NodeSettingsPanel
              node={selectedNode}
              strategyId={currentStrategyId}
              onSettingsChange={(updatedSettings) => {
                // Save current state to history before making changes
                saveToHistory();

                // Check if there are any newly activated API connections
                if (selectedNode.type === "fundamentalAnalyst") {
                  const oldSettings = selectedNode.data?.settings || {};
                  const oldSocialMedia =
                    oldSettings.socialMediaConnections || {};
                  const oldMarketData = oldSettings.marketDataConnections || {};
                  const oldCryptoAnalytics =
                    oldSettings.cryptoAnalyticsConnections || {};
                  const newSocialMedia =
                    updatedSettings.socialMediaConnections || {};
                  const newMarketData =
                    updatedSettings.marketDataConnections || {};
                  const newCryptoAnalytics =
                    updatedSettings.cryptoAnalyticsConnections || {};

                  // Handle social media API connections
                  Object.entries(newSocialMedia).forEach(
                    ([apiId, isConnected]) => {
                      // If this API was just activated (wasn't active before)
                      if (isConnected && !oldSocialMedia[apiId]) {
                        console.log(`Social media API activated: ${apiId}`);
                        // Call function to communicate with user API
                        activateAPIConnection("social_media", apiId);
                      }
                    },
                  );

                  // Handle market data API connections
                  Object.entries(newMarketData).forEach(
                    ([apiId, isConnected]) => {
                      // If this API was just activated (wasn't active before)
                      if (isConnected && !oldMarketData[apiId]) {
                        console.log(`Market data API activated: ${apiId}`);
                        // Call function to communicate with user API
                        activateAPIConnection("market_data", apiId);
                      }
                    },
                  );

                  // Handle crypto analytics API connections
                  Object.entries(newCryptoAnalytics).forEach(
                    ([apiId, isConnected]) => {
                      // If this API was just activated (wasn't active before)
                      if (isConnected && !oldCryptoAnalytics[apiId]) {
                        console.log(`Crypto analytics API activated: ${apiId}`);
                        // Call function to communicate with user API
                        activateAPIConnection("crypto_analytics", apiId);
                      }
                    },
                  );
                }

                // Update the node with new settings
                let updatedNodes = nodes.map((n) => {
                  if (n.id === selectedNode.id) {
                    return {
                      ...n,
                      data: {
                        ...n.data,
                        settings: updatedSettings,
                      },
                    };
                  }
                  return n;
                });

                // Check if symbol changed and propagate to downstream nodes
                const oldSymbol =
                  selectedNode.data?.settings?.symbol ||
                  selectedNode.data?.params?.symbol;
                const newSymbol = updatedSettings.symbol;

                if (newSymbol && newSymbol !== oldSymbol) {
                  console.log(
                    `Symbol changed from ${oldSymbol} to ${newSymbol}, propagating to downstream nodes`,
                  );
                  updatedNodes = propagateSymbolToDownstream(
                    selectedNode.id,
                    newSymbol,
                    updatedNodes,
                    edges,
                  );
                }

                // Check if Smart Symbol Discovery found a new symbol
                if (
                  updatedSettings.smartSymbolEnabled &&
                  updatedSettings.activeSmartSymbol
                ) {
                  const oldSmartSymbol =
                    selectedNode.data?.settings?.activeSmartSymbol;
                  if (updatedSettings.activeSmartSymbol !== oldSmartSymbol) {
                    console.log(
                      `Smart Symbol Discovery found: ${updatedSettings.activeSmartSymbol}, propagating to all nodes`,
                    );
                    setStrategySymbol(
                      updatedSettings.activeSmartSymbol,
                      selectedNode.id,
                    );
                  }
                }

                // Check if broker changed and propagate to downstream nodes
                const oldBroker =
                  selectedNode.data?.settings?.broker ||
                  selectedNode.data?.params?.broker;
                const newBroker = updatedSettings.broker;

                if (newBroker && newBroker !== oldBroker) {
                  console.log(
                    `Broker changed from ${oldBroker} to ${newBroker}, propagating to downstream nodes`,
                  );
                  updatedNodes = propagateBrokerToDownstream(
                    selectedNode.id,
                    newBroker,
                    updatedNodes,
                    edges,
                  );
                }

                setNodes(updatedNodes);
                setIsSettingsOpen(false);
              }}
              onClose={() => setIsSettingsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Clear confirmation dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={cancelClearCanvas}
        maxWidth="sm"
        fullWidth
        aria-labelledby="clear-dialog-title"
        disableScrollLock
      >
        <DialogTitle id="clear-dialog-title">Clear Canvas</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear the entire canvas? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClearCanvas} color="primary">
            Cancel
          </Button>
          <Button
            onClick={confirmClearCanvas}
            color="error"
            variant="contained"
          >
            Clear Canvas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog
        open={isTemplateDialogOpen}
        onClose={handleCloseTemplateDialog}
        maxWidth="md"
        fullWidth
        aria-labelledby="template-dialog-title"
        disableScrollLock
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle id="template-dialog-title" sx={{ pb: 0 }}>
          Select Strategy Template
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {/* Category Tabs */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              py: 2,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              mb: 2,
            }}
          >
            {[
              { id: "all", label: "All", icon: "📋" },
              { id: "Basic", label: "Basic", icon: "⚡" },
              { id: "General", label: "General", icon: "⚡" },
              { id: "Crypto", label: "Crypto", icon: "🪙" },
              { id: "Stocks", label: "Stocks", icon: "📈" },
              { id: "Forex", label: "Forex", icon: "💱" },
              { id: "Multi-Asset", label: "Multi-Asset", icon: "📊" },
              { id: "Advanced", label: "Advanced", icon: "🤖" },
            ].map((cat) => {
              const count =
                cat.id === "all"
                  ? Object.keys(strategyTemplates).length
                  : Object.values(strategyTemplates).filter(
                      (t) => (t.category || "General") === cat.id,
                    ).length;

              if (count === 0 && cat.id !== "all") return null;

              const getTabColor = (c) => {
                switch (c) {
                  case "Crypto":
                    return "#f7931a";
                  case "Stocks":
                    return "#4caf50";
                  case "Forex":
                    return "#2196f3";
                  case "Multi-Asset":
                    return "#9c27b0";
                  case "Advanced":
                    return "#ff5722";
                  case "all":
                    return "#1976d2";
                  default:
                    return "#757575";
                }
              };

              return (
                <Chip
                  key={cat.id}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <span>{cat.icon}</span>
                      <span>{cat.label}</span>
                      <Box
                        component="span"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          borderRadius: "10px",
                          px: 0.8,
                          py: 0.1,
                          fontSize: "0.75rem",
                          ml: 0.5,
                        }}
                      >
                        {count}
                      </Box>
                    </Box>
                  }
                  onClick={() => setTemplateCategoryTab(cat.id)}
                  sx={{
                    cursor: "pointer",
                    bgcolor:
                      templateCategoryTab === cat.id
                        ? getTabColor(cat.id)
                        : "rgba(255,255,255,0.1)",
                    color: "white",
                    fontWeight:
                      templateCategoryTab === cat.id ? "bold" : "normal",
                    "&:hover": {
                      bgcolor:
                        templateCategoryTab === cat.id
                          ? getTabColor(cat.id)
                          : "rgba(255,255,255,0.2)",
                    },
                    transition: "all 0.2s",
                  }}
                />
              );
            })}
          </Box>

          <Grid container spacing={2}>
            {Object.entries(strategyTemplates)
              .filter(
                ([, template]) =>
                  templateCategoryTab === "all" ||
                  (template.category || "General") === templateCategoryTab,
              )
              .map(([id, template]) => {
                const category = template.category || "General";
                const getCategoryColor = (c) => {
                  switch (c) {
                    case "Crypto":
                      return "#f7931a";
                    case "Stocks":
                      return "#4caf50";
                    case "Forex":
                      return "#2196f3";
                    case "Multi-Asset":
                      return "#9c27b0";
                    case "Advanced":
                      return "#ff5722";
                    default:
                      return "#757575";
                  }
                };
                return (
                  <Grid item xs={12} sm={6} md={4} key={id}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        border:
                          selectedTemplate === id
                            ? `2px solid ${getCategoryColor(category)}`
                            : "1px solid rgba(255,255,255,0.1)",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 4px 12px ${getCategoryColor(category)}40`,
                        },
                      }}
                      onClick={() => handleSelectTemplate(id)}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: "0.95rem",
                              fontWeight: "bold",
                              flex: 1,
                            }}
                          >
                            {template.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={category}
                            sx={{
                              bgcolor: getCategoryColor(category),
                              color: "white",
                              fontSize: "0.65rem",
                              height: 20,
                              ml: 1,
                            }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, minHeight: 36, fontSize: "0.8rem" }}
                        >
                          {template.description}
                        </Typography>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          <Chip
                            size="small"
                            label={`${template.nodes.length} Nodes`}
                            icon={
                              <AccountTreeIcon
                                sx={{ fontSize: "0.9rem !important" }}
                              />
                            }
                            sx={{
                              bgcolor: "rgba(255,255,255,0.1)",
                              fontSize: "0.7rem",
                              height: 24,
                            }}
                          />
                          {template.nodes.some(
                            (n) => n.data?.settings?.broker,
                          ) && (
                            <Chip
                              size="small"
                              label={
                                template.nodes.find(
                                  (n) => n.data?.settings?.broker,
                                )?.data?.settings?.broker
                              }
                              sx={{
                                bgcolor: getCategoryColor(category),
                                color: "white",
                                fontSize: "0.7rem",
                                height: 24,
                              }}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
          <Button
            onClick={handleLoadTemplate}
            variant="contained"
            color="primary"
            disabled={!selectedTemplate}
          >
            Load Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Strategy Manager Dialog */}
      <Dialog
        open={isStrategyManagerOpen}
        onClose={handleCloseStrategyManager}
        maxWidth="md"
        fullWidth
        aria-labelledby="strategy-manager-dialog-title"
        disableScrollLock
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle id="strategy-manager-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FolderOpenIcon sx={{ mr: 1 }} />
            Strategy Manager
          </Box>
        </DialogTitle>
        <DialogContent>
          {strategies.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No saved strategies found. Create a strategy and save it to see
                it here.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Last Updated</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {strategies.map((strategy) => (
                    <TableRow key={strategy.id} hover>
                      <TableCell padding="checkbox">
                        <IconButton
                          onClick={() => handleToggleFavorite(strategy.id)}
                          size="small"
                        >
                          {strategy.favorite ? (
                            <StarIcon fontSize="small" color="warning" />
                          ) : (
                            <StarBorderIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{strategy.name}</TableCell>
                      <TableCell>{strategy.description}</TableCell>
                      <TableCell>{strategy.lastUpdated || "N/A"}</TableCell>
                      <TableCell>
                        {strategy.tags &&
                          strategy.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleLoadStrategy(strategy.id)}
                          title="Load Strategy"
                        >
                          <LaunchIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteStrategy(strategy.id)}
                          title="Delete Strategy"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStrategyManager}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Strategy Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="save-strategy-dialog-title"
        disableScrollLock
      >
        <DialogTitle id="save-strategy-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SaveIcon sx={{ mr: 1 }} />
            Save Strategy
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <TextField
              label="Strategy Name"
              fullWidth
              margin="normal"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={strategyDescription}
              onChange={(e) => setStrategyDescription(e.target.value)}
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", mb: 1 }}>
                {strategyTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleDeleteTag(tag)}
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
              <TextField
                label="Add a tag"
                size="small"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddTag(e.target.value);
                    e.target.value = "";
                  }
                }}
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <NoteAltIcon
                  fontSize="small"
                  sx={{ mr: 1, verticalAlign: "middle" }}
                />
                Strategy Notes
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Add notes, documentation, or any important information about this strategy..."
                value={strategyNotes}
                onChange={(e) => setStrategyNotes(e.target.value)}
                variant="outlined"
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <NotificationsIcon
                  fontSize="small"
                  sx={{ mr: 1, verticalAlign: "middle" }}
                />
                Reporting Settings
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={enableReporting}
                    onChange={(e) => setEnableReporting(e.target.checked)}
                  />
                }
                label="Enable strategy reporting"
              />

              {enableReporting && (
                <Box sx={{ mt: 2, ml: 3 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Report Frequency</FormLabel>
                    <RadioGroup
                      value={reportFrequency}
                      onChange={(e) => setReportFrequency(e.target.value)}
                    >
                      <FormControlLabel
                        value="hourly"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">Hourly</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="daily"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">Daily</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        value="weekly"
                        control={<Radio size="small" />}
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <DateRangeIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">Weekly</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>

                  <FormControl sx={{ mt: 2, width: "100%" }}>
                    <FormLabel component="legend">
                      Notification Methods
                    </FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enableTelegram}
                            onChange={(e) =>
                              setEnableTelegram(e.target.checked)
                            }
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <TelegramIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">Telegram</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enableEmail}
                            onChange={(e) => setEnableEmail(e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">Email</Typography>
                          </Box>
                        }
                      />
                    </FormGroup>
                  </FormControl>

                  {enableTelegram && (
                    <TextField
                      label="Telegram Bot Token"
                      fullWidth
                      margin="normal"
                      size="small"
                      value={telegramToken}
                      onChange={(e) => setTelegramToken(e.target.value)}
                    />
                  )}

                  {enableTelegram && (
                    <TextField
                      label="Telegram Chat ID"
                      fullWidth
                      margin="normal"
                      size="small"
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                    />
                  )}

                  {enableEmail && (
                    <TextField
                      label="Email Address"
                      fullWidth
                      margin="normal"
                      size="small"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveDialog}>Cancel</Button>
          <Button
            disabled={isSaving}
            onClick={handleSaveStrategy}
            variant="contained"
            color="primary"
            startIcon={
              isSaving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
          >
            {isSaving ? "Saving..." : "Save Strategy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog
        open={saveTemplateDialogOpen}
        onClose={() => setSaveTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <BookmarkBorderIcon sx={{ mr: 1, color: 'warning.main' }} />
            Save Strategy as Template
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Save this strategy configuration as a reusable template. You can later create new strategies from this template.
          </DialogContentText>
          <TextField
            label="Template Name"
            fullWidth
            margin="normal"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            autoFocus
          />
          <TextField
            label="Description (optional)"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveAsTemplate}
            variant="contained"
            color="warning"
            disabled={!templateName.trim()}
            startIcon={<BookmarkIcon />}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Strategy Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ContentCopyIcon sx={{ mr: 1, color: 'info.main' }} />
            Clone Strategy
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a duplicate of this strategy with all its nodes, edges, and settings.
          </DialogContentText>
          <TextField
            label="New Strategy Name"
            fullWidth
            margin="normal"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCloneStrategy}
            variant="contained"
            color="info"
            startIcon={<ContentCopyIcon />}
          >
            Clone
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load from Template Dialog */}
      <Dialog
        open={loadTemplateDialogOpen}
        onClose={() => setLoadTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LibraryBooksIcon sx={{ mr: 1, color: 'primary.main' }} />
            Create Strategy from Template
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a new strategy from the saved template. You can optionally rename it.
          </DialogContentText>
          <TextField
            label="Strategy Name (optional — defaults to template name)"
            fullWidth
            margin="normal"
            value={loadTemplateName}
            onChange={(e) => setLoadTemplateName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLoadFromTemplate}
            variant="contained"
            color="primary"
            startIcon={<CloudDownloadIcon />}
          >
            Create Strategy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StrategyBuilder;
